import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import institutionService from '../services/institutionService';
import type { InstitutionDTO } from '../services/institutionService';
import { getCurrentUserData } from '../services/userService';
import { getCurrentUser } from '../services/authService';
import { useAuth } from '../context/AuthContext';
import { Permission } from '../types/user.types';
import { lookupCep } from '../services/cepService';
import {
  formatCep,
  formatCnpj,
  formatPhoneBR,
  isValidCnpj,
  isValidEmail,
  isValidUrl,
  onlyDigits,
  toUpperText,
} from '../utils/formatters';
import dirigenteService from '../services/dirigenteService';
import type { Dirigente } from '../types/dirigente.types';
import {
  SEXO_OPTIONS,
  ESTADO_CIVIL_OPTIONS,
  CARGO_OPTIONS,
  UF_OPTIONS
} from '../types/dirigente.types';
import documentoService from '../services/documentoService';
import type { DocumentoInstitucional } from '../types/documento.types';
import { TIPOS_DOCUMENTO, formatFileSize, getFileIcon } from '../types/documento.types';
import documentoInstitucionalService from '../services/documentoInstitucionalService';
import type { DocumentoInstitucional as DocInstitucional, TipoDocumentoInstitucional } from '../types/documentoInstitucional.types';
import documentoPessoalService from '../services/documentoPessoalService';
import type { DocumentoPessoalDTO } from '../types/documentoPessoal.types';
import FileViewerModal from '../components/FileViewerModal';
import { downloadAuthenticatedFile } from '../utils/authenticatedFile';
import { openAuthenticatedTab } from '../utils/openAuthenticatedTab';
import {
  TIPOS_DOCUMENTO_LABELS,
  DOCUMENTOS_OBRIGATORIOS,
  DOCUMENTOS_INSTITUICAO,
  TIPOS_DECLARACAO,
  FORMATOS_ACEITOS,
  TAMANHO_MAXIMO_BYTES,
  TAMANHO_MAXIMO_MB,
  formatFileSize as formatFileSize2,
  getFileIcon as getFileIcon2,
  calcularDiasParaVencimento,
} from '../types/documentoInstitucional.types';
import {
  StatusOSC,
  STATUS_OSC_LABELS,
  STATUS_OSC_COLORS,
} from '../types/statusOSC.types';
import {
  buildInstitutionDocumentStatusSummary,
  getDocumentosAtuaisPorTipo,
  renderDocStatusBadge,
  getStatusMessage,
  isDocumentoAprovado,
  isDocumentoReprovado,
  isDocumentoSubstituido,
  isDocumentoValidoParaObrigatorio,
  isDocumentoVencido,
  shouldSubstituirDocumento,
} from '../utils/statusOSC.utils';
import { StatusOSCBadge, StatusOSCPanel } from '../components/StatusOSCComponents';
import tipoDocumentoConfigService from '../services/tipoDocumentoConfigService';
import type { TipoDocumentoConfig } from '../services/tipoDocumentoConfigService';
import DocumentosPessoaisUpload from '../components/DocumentosPessoaisUpload';
import funcaoOrcamentariaService, { type FuncaoOrcamentariaDTO } from '../services/funcaoOrcamentariaService';
import Toast from '../components/Toast';
import { BASENAME } from '../services/api';
import diligenciaDocumentoService, { type DiligenciaDocumentoDTO } from '../services/diligenciaDocumentoService';
import { UI_THEME } from '../ui/theme';
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ArrowUpTrayIcon,
  CheckIcon,
  DocumentIcon,
  EyeIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { notifyError } from '../utils/notify';

const UFS = [
  'AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO',
];

type DocumentoLocalizacaoInfo = {
  titulo: string;
  descricao: string;
  orientacao: string;
  url?: string;
  ctaLabel?: string;
  accentClassName: string;
};

const DOCUMENTO_LOCALIZACAO_INFO: Partial<Record<TipoDocumentoInstitucional, DocumentoLocalizacaoInfo>> = {
  CARTAO_CNPJ: {
    titulo: 'Cartao CNPJ',
    descricao: 'Emissao e consulta do comprovante de inscricao e de situacao cadastral.',
    orientacao: 'Acesse o portal da Receita Federal para emitir o cartao do CNPJ da instituicao.',
    url: 'https://solucoes.receita.fazenda.gov.br/servicos/cnpjreva/cnpjreva_solicitacao.asp',
    ctaLabel: 'Abrir portal da Receita Federal',
    accentClassName: 'bg-blue-50 border-blue-200 text-blue-900',
  },
  ESTATUTO_SOCIAL: {
    titulo: 'Estatuto Social',
    descricao: 'Documento registrado nos atos constitutivos da instituicao.',
    orientacao: 'Renova no Expediente e Atos. Consulte o setor responsavel pelo registro dos atos da instituicao.',
    accentClassName: 'bg-emerald-50 border-emerald-200 text-emerald-900',
  },
  ALVARA_FUNCIONAMENTO: {
    titulo: 'Alvara de Funcionamento',
    descricao: 'Emissao e renovacao do alvara municipal de funcionamento.',
    orientacao: 'Utilize o portal tributario da Prefeitura de Sinop para emitir ou renovar o alvara.',
    url: 'https://www.gp.srv.br/tributario/sinop/portal_serv_servico?10,31',
    ctaLabel: 'Abrir portal da Prefeitura',
    accentClassName: 'bg-violet-50 border-violet-200 text-violet-900',
  },
  DECLARACAO_UTILIDADE_PUBLICA: {
    titulo: 'Declaracao de Utilidade Publica',
    descricao: 'Documento vinculado aos atos oficiais da instituicao.',
    orientacao: 'Renova no Expediente e Atos. Verifique o registro ou a publicacao oficial correspondente.',
    accentClassName: 'bg-amber-50 border-amber-200 text-amber-900',
  },
  ATA_ELEICAO_DIRETORIA: {
    titulo: 'Ata da Ultima Eleicao de Diretoria',
    descricao: 'Documento interno com a formalizacao da eleicao da diretoria vigente.',
    orientacao: 'Solicite a ata mais recente no arquivo interno da instituicao.',
    accentClassName: 'bg-indigo-50 border-indigo-200 text-indigo-900',
  },
  COMPROVANTE_INSCRICAO_CONSELHO: {
    titulo: 'Comprovante de Inscricao no Conselho',
    descricao: 'Comprovante de inscricao emitido pelo conselho competente.',
    orientacao: 'Renova no conselho da area de atuacao. Consulte o setor responsavel pela inscricao da entidade.',
    accentClassName: 'bg-rose-50 border-rose-200 text-rose-900',
  },
  COMPROVANTE_ENDERECO_INSTITUICAO: {
    titulo: 'Comprovante de Endereco',
    descricao: 'Documento que comprove o endereco atual da instituicao.',
    orientacao: 'Utilize um comprovante recente, como conta de agua, energia, telefone ou documento equivalente.',
    accentClassName: 'bg-slate-50 border-slate-200 text-slate-900',
  },
  CERTIDAO_TRIBUTOS_FEDERAIS: {
    titulo: 'Certidao Negativa de Tributos Federais',
    descricao: 'Consulta e emissao da certidao federal da pessoa juridica.',
    orientacao: 'Emita a certidao no portal da Receita Federal usando o CNPJ da instituicao.',
    url: 'https://servicos.receitafederal.gov.br/servico/certidoes/#/home/cnpj',
    ctaLabel: 'Abrir servico de certidoes federais',
    accentClassName: 'bg-orange-50 border-orange-200 text-orange-900',
  },
  CERTIDAO_TRIBUTOS_ESTADUAIS_PGE: {
    titulo: 'Certidao Negativa de Tributos Estaduais',
    descricao: 'Emissao da certidao estadual para Mato Grosso.',
    orientacao: 'Acesse o portal da SEFAZ/MT para consultar e emitir a certidao estadual.',
    url: 'https://www.sefaz.mt.gov.br/cnd/certidao/servlet/ServletRotd?origem=60',
    ctaLabel: 'Abrir portal da SEFAZ/MT',
    accentClassName: 'bg-teal-50 border-teal-200 text-teal-900',
  },
  CERTIFICADO_FGTS: {
    titulo: 'Certidao de Regularidade do FGTS',
    descricao: 'Consulta da regularidade do empregador junto ao FGTS.',
    orientacao: 'Emita o certificado no portal da Caixa informando os dados da instituicao.',
    url: 'https://consulta-crf.caixa.gov.br/consultacrf/pages/consultaEmpregador.jsf',
    ctaLabel: 'Abrir consulta do FGTS',
    accentClassName: 'bg-pink-50 border-pink-200 text-pink-900',
  },
  CERTIDAO_DEBITOS_TRABALHISTAS: {
    titulo: 'Certidao Negativa de Debitos Trabalhistas',
    descricao: 'Consulta da CNDT da instituicao.',
    orientacao: 'A emissao pode ser feita diretamente no portal do TST.',
    url: 'https://cndt-certidao.tst.jus.br/inicio.faces',
    ctaLabel: 'Abrir portal do TST',
    accentClassName: 'bg-cyan-50 border-cyan-200 text-cyan-900',
  },
  CERTIDAO_DEBITOS_MUNICIPAIS: {
    titulo: 'Certidao Negativa de Debitos Municipais',
    descricao: 'Emissao da certidao municipal da Prefeitura de Sinop.',
    orientacao: 'Use o portal tributario municipal para emitir a certidao negativa.',
    url: 'https://www.gp.srv.br/tributario/sinop/portal_serv_servico?12,53',
    ctaLabel: 'Abrir portal municipal',
    accentClassName: 'bg-lime-50 border-lime-200 text-lime-900',
  },
  CERTIDAO_TCE_INSTITUICAO: {
    titulo: 'Certidao Negativa de TCE/MT',
    descricao: 'Consulta da regularidade da instituicao perante o Tribunal de Contas.',
    orientacao: 'Emita a certidao no portal oficial do TCE/MT.',
    url: 'https://servicos.tce.mt.gov.br/certidao/emissao',
    ctaLabel: 'Abrir portal do TCE/MT',
    accentClassName: 'bg-fuchsia-50 border-fuchsia-200 text-fuchsia-900',
  },
};

type DirigenteDocumentosPendencia = {
  dirigenteId: string;
  nomeCompleto: string;
  cargo: string;
  cargos: string[];
  faltantes: string[];
  enviadosPendentes: string[];
  reprovados: string[];
  vencidos: string[];
};

type DocumentoPessoalTipoConfig = {
  tipo: string;
  nome: string;
  obrigatorio: boolean;
};

type ComplianceSeverity = 'success' | 'warning' | 'danger';

const REQUIRED_INSTITUTION_FIELDS: Array<{
  key: keyof InstitutionDTO;
  label: string;
  validate?: (value: unknown) => boolean;
}> = [
  { key: 'razaoSocial', label: 'Razão social' },
  { key: 'cnpj', label: 'CNPJ', validate: (value) => onlyDigits(String(value || '')).length === 14 },
  { key: 'inscricaoMunicipal', label: 'Inscrição municipal' },
  { key: 'telefone', label: 'Telefone' },
  { key: 'emailInstitucional', label: 'E-mail institucional', validate: (value) => isValidEmail(String(value || '')) },
  { key: 'cep', label: 'CEP', validate: (value) => onlyDigits(String(value || '')).length === 8 },
  { key: 'logradouro', label: 'Logradouro' },
  { key: 'numero', label: 'Número' },
  { key: 'bairro', label: 'Bairro' },
  { key: 'cidade', label: 'Cidade' },
  { key: 'uf', label: 'UF' },
  { key: 'numeroRegistroConselhoMunicipal', label: 'Registro no conselho municipal' },
  { key: 'objetoSocial', label: 'Objeto social' },
];

const INSTITUTION_FORM_SECTIONS: Array<{
  title: string;
  keys: Array<keyof InstitutionDTO>;
}> = [
  { title: 'Dados Básicos', keys: ['razaoSocial', 'cnpj', 'inscricaoMunicipal'] },
  { title: 'Dados de Contato', keys: ['telefone', 'emailInstitucional'] },
  { title: 'Endereço', keys: ['cep', 'logradouro', 'numero', 'bairro', 'cidade', 'uf'] },
  { title: 'Informações Adicionais', keys: ['numeroRegistroConselhoMunicipal', 'objetoSocial'] },
];

const normalizeCargo = (value?: string) =>
  (value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();

const getSeverityClasses = (severity: ComplianceSeverity, active: boolean) => {
  if (severity === 'danger') {
    return active
      ? 'border-red-500 text-red-700'
      : 'border-transparent text-red-600 hover:text-red-700 hover:border-red-300';
  }
  if (severity === 'warning') {
    return active
      ? 'border-orange-500 text-orange-700'
      : 'border-transparent text-orange-600 hover:text-orange-700 hover:border-orange-300';
  }
  return active
    ? 'border-green-500 text-green-700'
    : 'border-transparent text-green-600 hover:text-green-700 hover:border-green-300';
};

const getSeverityDotClass = (severity: ComplianceSeverity) => {
  if (severity === 'danger') return 'bg-red-500';
  if (severity === 'warning') return 'bg-orange-500';
  return 'bg-green-500';
};

const TIPOS_DOCUMENTO_PESSOAL_PRESIDENTE: DocumentoPessoalTipoConfig[] = [
  { tipo: 'RG_PRESIDENTE', nome: 'RG', obrigatorio: true },
  { tipo: 'CNH_PRESIDENTE', nome: 'CNH', obrigatorio: true },
  { tipo: 'DOCUMENTO_FOTO_PRESIDENTE', nome: 'Documento com Foto', obrigatorio: true },
  { tipo: 'COMPROVANTE_ENDERECO_PRESIDENTE', nome: 'Comprovante de Endereço', obrigatorio: true },
  { tipo: 'CERTIDAO_TCE_PRESIDENTE', nome: 'Certidão Negativa TCE', obrigatorio: true },
];

const TIPOS_DOCUMENTO_PESSOAL_DIRIGENTE: DocumentoPessoalTipoConfig[] = [
  { tipo: 'DOCUMENTO_FOTO_DIRIGENTE', nome: 'Documento de Identificação com Foto', obrigatorio: true },
  { tipo: 'CERTIDAO_TCE_DIRIGENTE', nome: 'Certidão Negativa TCE', obrigatorio: true },
];

const getTiposDocumentoPessoalObrigatorios = (cargo?: string): DocumentoPessoalTipoConfig[] =>
  cargo?.toLowerCase().includes('presidente')
    ? TIPOS_DOCUMENTO_PESSOAL_PRESIDENTE
    : TIPOS_DOCUMENTO_PESSOAL_DIRIGENTE;

const getDocumentosPessoaisAtuaisPorTipo = (documentos: DocumentoPessoalDTO[]): Map<string, DocumentoPessoalDTO> => {
  const documentosPorTipo = new Map<string, DocumentoPessoalDTO>();

  documentos.forEach((documento) => {
    const existente = documentosPorTipo.get(documento.tipoDocumento);
    const dataAtual = new Date(documento.dataUpload || 0).getTime();
    const dataExistente = existente ? new Date(existente.dataUpload || 0).getTime() : 0;

    if (!existente || dataAtual >= dataExistente) {
      documentosPorTipo.set(documento.tipoDocumento, documento);
    }
  });

  return documentosPorTipo;
};


type FormState = Partial<InstitutionDTO>;

const CadastroDadosInstitucionaisPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshUser, user, hasPermission, isOperadorSecretaria } = useAuth();
  const editId = searchParams.get('id');

  useEffect(() => {
    const instituicaoExistente = user?.instituicoes?.[0];
    if (!editId && isOperadorSecretaria && instituicaoExistente) {
      navigate(`/dashboard/cadastro-dados-institucionais?id=${encodeURIComponent(instituicaoExistente)}`, { replace: true });
    }
  }, [editId, isOperadorSecretaria, navigate, user?.instituicoes]);

  // Support ?tab=planos (or other tab names) so returning from sub-pages lands on the right tab
  const tabParam = searchParams.get("tab") as "instituicao" | "dirigentes" | "documentos" | "declaracoes" | "planos" | null;

  // Duas permissoes distintas no backend (InstitutionResource): puxar-para-analise
  // usa INICIAR_ANALISE_INSTITUICAO, aprovar/reprovar usa APPROVE_INSTITUTION. Antes
  // as duas caiam no mesmo hardcode de role, entao conceder uma sem a outra pela
  // tela de admin nao tinha efeito nenhum aqui.
  const canIniciarAnalise = hasPermission(Permission.INICIAR_ANALISE_INSTITUICAO);
  const canAprovarReprovarInstituicao = hasPermission(Permission.APPROVE_INSTITUTION);

  const [iniciandoAnalise, setIniciandoAnalise] = useState(false);
  const [reenviandoAnalise, setReenviandoAnalise] = useState(false);
  const [aprovandoInstituicao, setAprovandoInstituicao] = useState(false);
  const [reprovandoInstituicao, setReprovandoInstituicao] = useState(false);
  const [showReprovacaoModal, setShowReprovacaoModal] = useState(false);
  const [motivoReprovacao, setMotivoReprovacao] = useState('');
  const [bloqueando, setBloqueando] = useState(false);
  const [desbloqueando, setDesbloqueando] = useState(false);
  const [showBloqueioModal, setShowBloqueioModal] = useState(false);
  const [motivoBloqueio, setMotivoBloqueio] = useState('');

  const [loadingExisting, setLoadingExisting] = useState(false);

  // Nova lÃ³gica: controle de exibiÃ§Ã£o do formulÃ¡rio
  const [cnpjSearchStatus, setCnpjSearchStatus] = useState<'initial' | 'searching' | 'found' | 'notFound'>('initial');
  const [foundInstitution, setFoundInstitution] = useState<InstitutionDTO | null>(null);

  const [form, setForm] = useState<FormState>({
     razaoSocial: '',
     nomeFantasia: '',
     cnpj: '',
     inscricaoEstadual: '',
     inscricaoMunicipal: '',
     dataFundacao: '',
     areasAtuacao: [],

     telefone: '',
     celular: '',
     emailInstitucional: '',
     emailSecundario: '',
     website: '',

     cep: '',
     logradouro: '',
     numero: '',
     complemento: '',
     bairro: '',
     cidade: '',
     uf: 'MT',
     pontoReferencia: '',

     numeroRegistroConselhoMunicipal: '',
     dataRegistroConselho: '',
     objetoSocial: '',
     quantidadeBeneficiarios: undefined,
  });

  const setField = <K extends keyof FormState>(field: K, value: FormState[K]) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));

    setErrors((prev) => {
      if (!prev[field as string]) return prev;
      const next = { ...prev };
      delete next[field as string];
      return next;
    });
  };

  const [saving, setSaving] = useState(false);
  const [loadingCep, setLoadingCep] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  // Form-level validation errors for the institution form
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Derived flag used before submit to short-circuit when validation errors exist
  const hasErrors = useMemo(() => Object.keys(errors).length > 0, [errors]);
  // Toast notifications local to this page
  const [toast, setToast] = useState<{ message: string; type?: 'success' | 'error' | 'info' } | null>(null);
  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'info') => setToast({ message, type });
  const isSaas = UI_THEME === 'saas';
  const accentTextClass = isSaas ? 'text-teal-700 dark:text-teal-300' : 'text-blue-700 dark:text-blue-300';
  const headerClass = isSaas
    ? 'rounded-2xl border border-teal-200/70 bg-gradient-to-br from-white via-teal-50/70 to-indigo-50 p-4 dark:border-teal-500/20 dark:from-slate-950 dark:via-slate-900 dark:to-teal-950/40'
    : 'rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950';
  const panelClass = isSaas
    ? 'rounded-2xl border border-teal-200/70 bg-white dark:border-teal-500/20 dark:bg-slate-950'
    : 'rounded-2xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-950';
  const sectionTitleClass = 'text-base font-semibold text-slate-950 dark:text-white';
  const mutedTextClass = 'text-sm text-slate-600 dark:text-slate-400';
  const inputBaseClass = 'w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 transition-colors focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 disabled:bg-slate-100 disabled:text-slate-500 dark:border-slate-700 dark:bg-slate-900 dark:text-white dark:disabled:bg-slate-800';
  const secondaryButtonClass = 'inline-flex items-center justify-center rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800';
  const primaryButtonClass = isSaas
    ? 'inline-flex items-center justify-center rounded-xl bg-teal-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-60'
    : 'inline-flex items-center justify-center rounded-xl bg-blue-700 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-60';

  // Estados para abas e dirigentes
  const [activeTab, setActiveTab] = useState<"instituicao" | "dirigentes" | "documentos" | "declaracoes" | "planos">(
    tabParam ?? 'instituicao'
  );

  // Keep activeTab in sync when the URL ?tab= param changes (e.g. back-navigation from sub-pages)
  useEffect(() => {
    if (tabParam) setActiveTab(tabParam);
  }, [tabParam]);
  const [dirigentes, setDirigentes] = useState<Dirigente[]>([]);
  const [loadingDirigentes, setLoadingDirigentes] = useState(false);
  const [showDirigenteModal, setShowDirigenteModal] = useState(false);
  const [editingDirigente, setEditingDirigente] = useState<Dirigente | null>(null);
  // Por padrão mostra só dirigentes ativos; o toggle na tela é "mostrar inativos também".
  const [apenasAtivos, setApenasAtivos] = useState(true);
  const [avisos, setAvisos] = useState<string[]>([]);

  // Inativação de vínculo (informar data de término do cargo nesta instituição)
  const [showInativarVinculoModal, setShowInativarVinculoModal] = useState(false);
  const [inativandoVinculo, setInativandoVinculo] = useState<Dirigente | null>(null);
  const [inativarVinculoData, setInativarVinculoData] = useState({ dataTermino: '', motivo: '' });
  const [inativarVinculoLoading, setInativarVinculoLoading] = useState(false);

  // Track whether the institution was persisted and the last saved snapshot
  const [savedInstitutionId, setSavedInstitutionId] = useState<string | null>(null);
  const [loadedSnapshot, setLoadedSnapshot] = useState<string>('');
  const currentInstitutionId = editId || savedInstitutionId || form.institutionId || '';

  // Read-only when viewing an existing record; editable for new records
  const [isEditMode, setIsEditMode] = useState<boolean>(!Boolean(editId));
  useEffect(() => { setIsEditMode(!Boolean(editId)); }, [editId]);

  // Estado que representa o status geral da OSC (documentaÃ§Ã£o) â€” usado no cabeÃ§alho e cÃ¡lculo de mensagens
  const [statusOSC, setStatusOSC] = useState<StatusOSC>(StatusOSC.EM_CADASTRO);

  const handleCancel = () => {
    if (loadedSnapshot) {
      try {
        const parsed = JSON.parse(loadedSnapshot) as FormState;
        setForm(prev => ({ ...prev, ...parsed }));
      } catch (e) {
        console.warn('Erro ao reverter snapshot:', e);
      }
    }
    setIsEditMode(false);
  };

  // Estados para validaÃ§Ã£o de dirigente
  const [dirigenteErrors, setDirigenteErrors] = useState<Record<string, string>>({});
  const [loadingCepDirigente, setLoadingCepDirigente] = useState(false);

  // Estados para documentos institucionais
  const [documentosInstitucionais, setDocumentosInstitucionais] = useState<DocInstitucional[]>([]);
  const [loadingDocumentos, setLoadingDocumentos] = useState(false);
  const [loadingDocumentosDirigentes, setLoadingDocumentosDirigentes] = useState(false);
  const [dirigenteDocumentosPendentes, setDirigenteDocumentosPendentes] = useState<DirigenteDocumentosPendencia[]>([]);
  const [uploadingFile, setUploadingFile] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const uploadFileInputRef = useRef<HTMLInputElement | null>(null);
    const [tipoDocumentoSelecionado, setTipoDocumentoSelecionado] = useState<TipoDocumentoInstitucional | ''>('');
    const [dataEmissao, setDataEmissao] = useState('');
    const [dataValidade, setDataValidade] = useState('');
    const [numeroDocumento, setNumeroDocumento] = useState('');
    const [observacoesDoc, setObservacoesDoc] = useState('');
    const [editingDocumento, setEditingDocumento] = useState<DocInstitucional | null>(null);
    const [showModalDocumento, setShowModalDocumento] = useState(false);

    // Estados para drawer de renovação de documento vencido
    const [diligencias, setDiligencias] = useState<DiligenciaDocumentoDTO[]>([]);
    const [diligenciasLidas, setDiligenciasLidas] = useState<Set<string>>(new Set());

    const [renovarDoc, setRenovarDoc] = useState<DocInstitucional | null>(null);
    const [renovarFile, setRenovarFile] = useState<File | null>(null);
    const [renovarDataEmissao, setRenovarDataEmissao] = useState('');
    const [renovarDataValidade, setRenovarDataValidade] = useState('');
    const [renovarNumeroDocumento, setRenovarNumeroDocumento] = useState('');
    const [renovarLoading, setRenovarLoading] = useState(false);
    const renovarFileInputRef = useRef<HTMLInputElement | null>(null);

    // Estados para upload de declarações
    const [uploadingDeclaracao, setUploadingDeclaracao] = useState(false);
    const [selectedFileDeclaracao, setSelectedFileDeclaracao] = useState<File | null>(null);
    const [activeDeclaracaoUpload, setActiveDeclaracaoUpload] = useState<TipoDocumentoInstitucional | null>(null);

    // Modal de informaÃ§Ãµes sobre links de renovaÃ§Ã£o de documentos
    const [showInfoModal, setShowInfoModal] = useState(false);

    // Modal e dados dos documentos pessoais do dirigente (missing state - added to prevent ReferenceError)
    const [showDocumentosPessoaisModal, setShowDocumentosPessoaisModal] = useState<boolean>(false);
    const [dirigenteDocumentosPessoais, setDirigenteDocumentosPessoais] = useState<Dirigente | null>(null);

    // Document type configurations (load before dashboard so dashboard can use them)
    const [documentConfigs, setDocumentConfigs] = useState<TipoDocumentoConfig[]>([]);
    const [loadingConfigs, setLoadingConfigs] = useState(false);
    const [selectedDocConfig, setSelectedDocConfig] = useState<TipoDocumentoConfig | null>(null);

    // FunÃ§Ãµes orÃ§amentÃ¡rias usadas em vÃ¡rios selects; defined here to avoid runtime ReferenceError
    const [funcoesOrcamentarias, setFuncoesOrcamentarias] = useState<FuncaoOrcamentariaDTO[]>([]);

    // File viewer state & helpers (missing previously, caused ReferenceError at runtime)
    const [showFileViewer, setShowFileViewer] = useState<boolean>(false);
    const [currentViewingDoc, setCurrentViewingDoc] = useState<DocInstitucional | null>(null);
    const [currentViewingIndex, setCurrentViewingIndex] = useState<number>(0);

    const selectedDocumentoInfo = useMemo(() => {
      if (!tipoDocumentoSelecionado) return null;
      return DOCUMENTO_LOCALIZACAO_INFO[tipoDocumentoSelecionado] ?? null;
    }, [tipoDocumentoSelecionado]);

    const handleViewDocumento = (doc: DocInstitucional) => {
      const idx = (documentosVisiveis || []).findIndex(d => d.id === doc.id);
      setCurrentViewingDoc(doc);
      setCurrentViewingIndex(idx >= 0 ? idx : 0);
      setShowFileViewer(true);
    };

    const handleCloseFileViewer = () => {
      setShowFileViewer(false);
      setCurrentViewingDoc(null);
    };

    const handlePreviousDocument = () => {
      const prev = currentViewingIndex - 1;
      if (prev >= 0) {
        setCurrentViewingIndex(prev);
        setCurrentViewingDoc(documentosVisiveis[prev]);
      }
    };

    const handleNextDocument = () => {
      const next = currentViewingIndex + 1;
      if (next < (documentosVisiveis || []).length) {
        setCurrentViewingIndex(next);
        setCurrentViewingDoc(documentosVisiveis[next]);
      }
    };

  // Derived list of required document types coming from dynamic configs (if present).
  // This avoids ReferenceError when other parts of the file reference `obrigatoriosFromConfig`.
  const obrigatoriosFromConfig = useMemo(() => {
    try {
      if (!Array.isArray(documentConfigs)) return [] as TipoDocumentoInstitucional[];
      // Consider only active configs when deriving required types. Inactive configs
      // shouldn't count as "obrigatÃ³rios" for the UI because we also filter inactive
      // types from the upload select.
      // Somente categorias de documentos da instituição contam aqui; documentos
      // pessoais de dirigente (categoria PESSOAL) são controlados na aba de
      // dirigentes e nunca aparecem em documentosInstitucionais — incluí-los
      // geraria uma pendência impossível de resolver. Mesmo filtro do backend
      // (StatusOSCService.getDocumentosObrigatoriosConfigurados).
      const CATEGORIAS_INSTITUCIONAIS = ['INSTITUCIONAL', 'CERTIDAO'];
      return documentConfigs
        .filter((c) => c.ativo !== false && Boolean((c as any).obrigatorio)
          && CATEGORIAS_INSTITUCIONAIS.includes(String((c as any).categoria || '').toUpperCase()))
        .map((c) => c.codigo as TipoDocumentoInstitucional);
    } catch (err) {
      return [] as TipoDocumentoInstitucional[];
    }
  }, [documentConfigs]);

  const documentStatusSummary = useMemo(
    () => buildInstitutionDocumentStatusSummary(documentosInstitucionais || [], {
      obrigatorios: obrigatoriosFromConfig,
      statusAtual: statusOSC,
    }),
    [documentosInstitucionais, obrigatoriosFromConfig, statusOSC]
  );
  const { documentosAtuaisPorTipo, documentosVisiveis, tiposObrigatoriosPendentes, dashboard } = documentStatusSummary;

  // Pendentes que aparecem no select de upload (exclui declarações, que ficam na aba própria)
  const tiposObrigatoriosPendentesNoSelect = useMemo(
    () => tiposObrigatoriosPendentes.filter((t) => !TIPOS_DECLARACAO.includes(t as TipoDocumentoInstitucional)),
    [tiposObrigatoriosPendentes]
  );

  // Contadores da aba "Documentos" — excluem declarações (que ficam na aba própria)
  const dashboardDocsTab = useMemo(() => {
    const obrigSource = obrigatoriosFromConfig.length > 0 ? obrigatoriosFromConfig : DOCUMENTOS_OBRIGATORIOS;
    const obrigNoDeclaracao = obrigSource.filter((t) => !TIPOS_DECLARACAO.includes(t as TipoDocumentoInstitucional));
    const enviados = obrigNoDeclaracao.filter((t) => isDocumentoValidoParaObrigatorio(documentosAtuaisPorTipo.get(t)?.statusDocumento)).length;
    const docsAtuaisNoDeclaracao = Array.from(documentosAtuaisPorTipo.values()).filter(
      (d) => !isDocumentoSubstituido(d.statusDocumento) && !TIPOS_DECLARACAO.includes(d.tipoDocumento as TipoDocumentoInstitucional)
    );
    return {
      obrigatoriosTotal: obrigNoDeclaracao.length,
      obrigatoriosEnviados: enviados,
      obrigatoriosFaltando: obrigNoDeclaracao.length - enviados,
      percentualCompleto: obrigNoDeclaracao.length > 0 ? Math.round((enviados / obrigNoDeclaracao.length) * 100) : 0,
      pendentes: docsAtuaisNoDeclaracao.filter((d) => !isDocumentoAprovado(d.statusDocumento) && !isDocumentoReprovado(d.statusDocumento) && d.statusDocumento !== 'VENCIDO').length,
      reprovados: docsAtuaisNoDeclaracao.filter((d) => isDocumentoReprovado(d.statusDocumento)).length,
      vencidos: docsAtuaisNoDeclaracao.filter((d) => d.statusDocumento === 'VENCIDO' || (d.dataValidade != null && calcularDiasParaVencimento(d.dataValidade as string) < 0)).length,
    };
  }, [obrigatoriosFromConfig, documentosAtuaisPorTipo]);

  const documentosDeclaracoes = useMemo(
    () => documentosVisiveis.filter((d) => TIPOS_DECLARACAO.includes(d.tipoDocumento as TipoDocumentoInstitucional)),
    [documentosVisiveis]
  );
  const documentosNaoDeclaracoes = useMemo(
    () => documentosVisiveis.filter((d) => !TIPOS_DECLARACAO.includes(d.tipoDocumento as TipoDocumentoInstitucional)),
    [documentosVisiveis]
  );

  const documentosAVencer = useMemo(
    () => documentosNaoDeclaracoes.filter(d => {
      if (d.statusDocumento === 'VENCIDO' || d.statusDocumento === 'SUBSTITUIDO' || d.statusDocumento === 'REPROVADO') return false;
      if (!d.dataValidade) return false;
      const dias = calcularDiasParaVencimento(d.dataValidade as string);
      return dias !== null && dias >= 0 && dias <= 30;
    }),
    [documentosNaoDeclaracoes]
  );

const DECLARACAO_TEMPLATES: Partial<Record<TipoDocumentoInstitucional, { url: string; label: string }>> = {
  DECLARACAO_CAPACIDADE_TECNICA: {
    url: "/templates/DECLARACAO_CAPACIDADE_TECNICA.docx",
    label: "Modelo (.docx)"
  },
  COMPROVANTE_EXPERIENCIA: {
    url: "/templates/COMPROVANTE_EXPERIENCIA.docx",
    label: "Exemplo (.docx)"
  },
  DECLARACAO_COMPOSICAO_QUADRO_DIRIGENTES: {
    url: "/templates/DECLARACAO_COMPOSICAO_QUADRO_DIRIGENTES.docx",
    label: "Modelo (.docx)"
  },
  RELACAO_NOMINAL_DIRIGENTES: {
    url: "/templates/RELACAO_NOMINAL_DIRIGENTES.docx",
    label: "Modelo (.docx)"
  },
  DECLARACAO_CONDICOES_INSTALACAO: {
    url: "/templates/DECLARACAO_CONDICOES_INSTALACAO.docx",
    label: "Modelo (.docx)"
  },
  DECLARACAO_NAO_INCORRE_IMPEDIMENTOS: {
    url: "/templates/DECLARACAO_NAO_INCORRE_IMPEDIMENTOS.docx",
    label: "Modelo (.docx)"
  },
  DECLARACAO_NAO_CONTRATACAO: {
    url: "/templates/DECLARACAO_NAO_CONTRATACAO.docx",
    label: "Modelo (.docx)"
  },
};

  const cadastroObrigatorioFaltantes = useMemo(
    () =>
      REQUIRED_INSTITUTION_FIELDS.filter(({ key, validate }) => {
        const value = form[key];
        if (validate) return !validate(value);
        return String(value ?? '').trim().length === 0;
      }).map(({ label }) => label),
    [form]
  );
  const dirigentesObrigatoriosFaltantes = useMemo(() => {
    const cargosAtivos = new Set(
      (dirigentes || [])
        .filter((dirigente: any) => String(dirigente?.statusCargo || 'Ativo').toLowerCase() !== 'inativo')
        .map((dirigente) => normalizeCargo(dirigente.cargo))
    );
    return ['Presidente', 'Tesoureiro'].filter((cargo) => !cargosAtivos.has(normalizeCargo(cargo)));
  }, [dirigentes]);
  const resumoPendenciasDirigentes = useMemo(
    () => dirigenteDocumentosPendentes.reduce(
      (acc, item) => ({
        faltantes: acc.faltantes + item.faltantes.length,
        pendentes: acc.pendentes + item.enviadosPendentes.length,
        reprovados: acc.reprovados + item.reprovados.length,
        vencidos: acc.vencidos + item.vencidos.length,
      }),
      { faltantes: 0, pendentes: 0, reprovados: 0, vencidos: 0 }
    ),
    [dirigenteDocumentosPendentes]
  );
  const totalPendenciasDirigentes = resumoPendenciasDirigentes.faltantes
    + resumoPendenciasDirigentes.pendentes
    + resumoPendenciasDirigentes.reprovados
    + resumoPendenciasDirigentes.vencidos;
  const pendenciasDirigentePorPessoa = useMemo(
    () => new Map(
      dirigenteDocumentosPendentes.map((item) => [item.dirigenteId, item] as const)
    ),
    [dirigenteDocumentosPendentes]
  );
  const totalPendenciasObrigatorias = cadastroObrigatorioFaltantes.length
    + dirigentesObrigatoriosFaltantes.length
    + dashboard.obrigatoriosFaltando
    + resumoPendenciasDirigentes.faltantes
    + dashboard.reprovados
    + dashboard.vencidos
    + resumoPendenciasDirigentes.reprovados
    + resumoPendenciasDirigentes.vencidos;
  const totalPendenciasAnalise = dashboard.pendentes + resumoPendenciasDirigentes.pendentes;
  const hasExplicitRejections = dashboard.reprovados > 0
    || dashboard.vencidos > 0
    || resumoPendenciasDirigentes.reprovados > 0
    || resumoPendenciasDirigentes.vencidos > 0;
  const complianceSeverity: ComplianceSeverity = totalPendenciasObrigatorias > 0
    ? hasExplicitRejections
      ? 'danger'
      : 'warning'
    : totalPendenciasAnalise > 0
      ? 'warning'
      : currentInstitutionId
        ? 'success'
        : 'danger';
  const effectiveStatusOSC = useMemo(() => {
    if (currentInstitutionId) {
      return statusOSC || StatusOSC.EM_CADASTRO;
    }
    if (statusOSC === StatusOSC.SUSPENSA || statusOSC === StatusOSC.INATIVA) return statusOSC;
    if (!currentInstitutionId && cadastroObrigatorioFaltantes.length > 0) return StatusOSC.EM_CADASTRO;
    if (totalPendenciasObrigatorias > 0) {
      return currentInstitutionId ? StatusOSC.DOCUMENTOS_INCOMPLETOS : StatusOSC.EM_CADASTRO;
    }
    if (totalPendenciasAnalise > 0) return StatusOSC.EM_ANALISE;
    return currentInstitutionId ? StatusOSC.APROVADO : StatusOSC.EM_CADASTRO;
  }, [
    cadastroObrigatorioFaltantes.length,
    currentInstitutionId,
    statusOSC,
    totalPendenciasAnalise,
    totalPendenciasObrigatorias,
  ]);
  const instituicaoTabSeverity: ComplianceSeverity = cadastroObrigatorioFaltantes.length > 0 ? 'warning' : 'success';
  const dirigentesTabSeverity: ComplianceSeverity =
    resumoPendenciasDirigentes.reprovados > 0 || resumoPendenciasDirigentes.vencidos > 0
      ? 'danger'
      : dirigentesObrigatoriosFaltantes.length > 0 || resumoPendenciasDirigentes.faltantes > 0
        ? 'warning'
      : resumoPendenciasDirigentes.pendentes > 0
        ? 'warning'
        : 'success';
  const documentosTabSeverity: ComplianceSeverity =
    dashboardDocsTab.reprovados > 0 || dashboardDocsTab.vencidos > 0
      ? 'danger'
      : dashboardDocsTab.obrigatoriosFaltando > 0
        ? 'warning'
      : dashboardDocsTab.pendentes > 0
        ? 'warning'
        : 'success';

  // Load document type configurations from API on mount
  useEffect(() => {
    let mounted = true;
    const loadConfigs = async () => {
      try {
        setLoadingConfigs(true);
        const configs = await tipoDocumentoConfigService.listar();
        if (!mounted) return;
        setDocumentConfigs(Array.isArray(configs) ? configs : []);

        // If a tipoDocumento is already selected (e.g. editing), keep selectedDocConfig in sync
        if (tipoDocumentoSelecionado) {
          const found = (Array.isArray(configs) ? configs : []).find(c => c.codigo === String(tipoDocumentoSelecionado));
          setSelectedDocConfig(found ?? null);
        }
      } catch (err) {
        console.error('Erro ao carregar tipos de documento:', err);
        setDocumentConfigs([]);
      } finally {
        if (mounted) setLoadingConfigs(false);
      }
    };
    loadConfigs();
    return () => { mounted = false; };
  }, []);

  // Load funÃ§Ãµes orÃ§amentÃ¡rias on mount
  useEffect(() => {
    let mounted = true;
    const loadFuncoes = async () => {
      try {
        const data = await funcaoOrcamentariaService.list();
        if (!mounted) return;
        setFuncoesOrcamentarias(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Erro ao carregar funÃ§Ãµes orÃ§amentÃ¡rias:', err);
        setFuncoesOrcamentarias([]);
      }
    };
    loadFuncoes();
    return () => { mounted = false; };
  }, []);

  const handleCnpjBlur = async () => {
    const cnpj = onlyDigits(form.cnpj || '');

    // Only search if CNPJ is valid (14 digits) and we're NOT in edit mode
    if (!cnpj || cnpj.length !== 14 || editId) {
      return;
    }

    // Don't search if CNPJ is invalid
    if (!isValidCnpj(cnpj)) {
      return;
    }

    try {
      setCnpjSearchStatus('searching');
      setLoadingExisting(true);
      console.log('[CadastroDados] Searching for CNPJ:', cnpj);

      const institution = await institutionService.getByCnpj(cnpj);

      console.log('[CadastroDados] Institution found:', institution);

      // CNPJ existe - mostrar botÃ£o de vincular
      setFoundInstitution(institution);
      setCnpjSearchStatus('found');

    } catch (error: any) {
      // 404 means not found - show form to create new institution
      if (error.response?.status === 404) {
        console.log('[CadastroDados] CNPJ not found - showing form to create new institution');
        setFoundInstitution(null);
        setCnpjSearchStatus('notFound');
        return;
      }

      // Other errors should be logged
      console.error('[CadastroDados] Error searching CNPJ:', error);
      showToast('Erro ao buscar CNPJ. Por favor, tente novamente.', 'error');
      setCnpjSearchStatus('initial');
    } finally {
      setLoadingExisting(false);
    }
  };

  // Nova funÃ§Ã£o: Vincular usuÃ¡rio Ã  instituiÃ§Ã£o existente
  const handleVincular = async () => {
    if (!foundInstitution) return;

    try {
      setSaving(true);
      console.log('[CadastroDados] Vinculando usuÃ¡rio Ã  instituiÃ§Ã£o:', foundInstitution.institutionId);

      // Usar endpoint dedicado de vinculaÃ§Ã£o (nÃ£o precisa enviar todos os campos)
      const result = await institutionService.vincularUsuario(foundInstitution.institutionId);

      console.log('[CadastroDados] VinculaÃ§Ã£o bem-sucedida:', result);

      // Atualizar dados do usuÃ¡rio
      await getCurrentUserData();
      refreshUser();

      showToast(result.message || 'Você foi vinculado à instituição com sucesso!', 'success');

      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);

    } catch (error: any) {
      console.error('[CadastroDados] Error linking user to institution:', error);
      const errorMessage = error?.response?.data?.error || 'Erro ao vincular usuário à instituição.';
      showToast(String(errorMessage), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleArea = (area: string) => {
    const current = form.areasAtuacao ?? [];
    if (current.includes(area)) {
      setField('areasAtuacao', current.filter((a) => a !== area));
    } else {
      setField('areasAtuacao', [...current, area]);
    }
  };

  const refreshInstitutionStatus = async (institutionId?: string) => {
    const id = institutionId || currentInstitutionId;
    if (!id) return;

    try {
      const updatedInstitution = await institutionService.getById(id);
      if (updatedInstitution?.statusOSC) {
        setStatusOSC(updatedInstitution.statusOSC);
      }
      setForm((prev) => ({
        ...prev,
        statusOSC: updatedInstitution?.statusOSC ?? prev.statusOSC,
        statusAprovacao: updatedInstitution?.statusAprovacao ?? prev.statusAprovacao,
      }));
    } catch (error) {
      console.error('Erro ao atualizar status da instituição:', error);
    }
  };

  const refreshInstitutionOverview = async (institutionId?: string) => {
    const id = institutionId || currentInstitutionId;
    if (!id) return;
    await Promise.all([
      loadDocumentos(),
      loadDirigentes(),
      refreshInstitutionStatus(id),
      loadDiligencias(),
    ]);
  };

  // FunÃ§Ãµes para gerenciar dirigentes
  const loadDirigentes = async () => {
    if (!currentInstitutionId) {
      setDirigentes([]);
      setDirigenteDocumentosPendentes([]);
      return;
    }

    try {
      setLoadingDirigentes(true);
      const data = await dirigenteService.listar(currentInstitutionId, apenasAtivos);
      const list = Array.isArray(data) ? data : [];

      // Some backends may return dirigentes WITH a `vinculos` array (one person with many vÃ­nculos).
      // To display each vÃ­nculo separately in the Diretoria dashboard, flatten those into
      // individual entries while keeping person fields (nome, cpf, email, telefone, idPessoa).
      const flattened: any[] = [];
      list.forEach((d: any) => {
        // If this item already represents a single vÃ­nculo (has cargo/dataInicioCargo), use it
        if (d && (d.cargo || d.dataInicioCargo) && !Array.isArray(d.vinculos)) {
          // treat as single vÃ­nculo
          flattened.push(d);
          return;
        }

        if (d && Array.isArray(d.vinculos) && d.vinculos.length > 0) {
          d.vinculos.forEach((v: any) => {
            const item = {
              // Use vÃ­nculo id if available to distinguish multiple vÃ­nculos of same person
              id: v.id ?? `${d.id || 'p'}-${v.instituicaoId || ''}-${v.cargo || ''}-${v.dataInicioCargo || ''}`,
              pessoaId: d.id,
              nomeCompleto: d.nomeCompleto || d.nome || '',
              cpf: d.cpf || '',
              email: d.email || d.emailInstitucional || '',
              telefone: d.telefone || '',
              // vÃ­nculo-specific
              cargo: v.cargo,
              dataInicioCargo: v.dataInicioCargo,
              dataTerminoCargo: v.dataTerminoCargo,
              instituicaoId: v.instituicaoId,
              statusCargo: v.statusCargo,
            };

            // if apenasAtivos filter is true, include only active vÃ­nculos
            if (apenasAtivos) {
              if (!item.statusCargo || String(item.statusCargo).toLowerCase() !== 'ativo') return;
            }

            flattened.push(item);
          });
        } else {
          // No vinculos array - push as-is
          flattened.push(d);
        }
      });

      setDirigentes(flattened);
      await loadDirigenteDocumentosPendentes(flattened);

       // Carregar avisos
       const response = await dirigenteService.verificarAvisos(editId);
       setAvisos(response.avisos);
    } catch (error) {
      console.error('Erro ao carregar dirigentes:', error);
      setDirigenteDocumentosPendentes([]);
    } finally {
      setLoadingDirigentes(false);
    }
  };

  const handleOpenInativarVinculo = (dirigente: Dirigente) => {
    setInativandoVinculo(dirigente);
    setInativarVinculoData({ dataTermino: '', motivo: '' });
    setShowInativarVinculoModal(true);
  };

  const handleConfirmInativarVinculo = async () => {
    if (!inativandoVinculo) return;
    if (!inativarVinculoData.dataTermino || !inativarVinculoData.motivo.trim()) {
      notifyError('Data de término e motivo são obrigatórios');
      return;
    }
    const dirigenteId = inativandoVinculo.pessoaId || inativandoVinculo.id;
    const instituicaoId = inativandoVinculo.instituicaoId || currentInstitutionId || '';
    if (!dirigenteId || !instituicaoId) {
      notifyError('Não foi possível identificar o dirigente ou a instituição.');
      return;
    }
    try {
      setInativarVinculoLoading(true);
      await dirigenteService.inativarVinculo(dirigenteId, {
        instituicaoId,
        cargo: inativandoVinculo.cargo,
        dataTermino: inativarVinculoData.dataTermino,
        motivo: inativarVinculoData.motivo.trim(),
      });
      setShowInativarVinculoModal(false);
      setInativandoVinculo(null);
      await loadDirigentes();
    } catch (error: any) {
      console.error('Erro ao inativar vínculo:', error);
      notifyError(error?.response?.data?.error || 'Erro ao inativar vínculo do dirigente');
    } finally {
      setInativarVinculoLoading(false);
    }
  };

  // Carregar diligências recebidas
  const loadDiligencias = async () => {
    if (!currentInstitutionId) return;
    try {
      const lista = await diligenciaDocumentoService.listar(currentInstitutionId);
      setDiligencias(Array.isArray(lista.data) ? lista.data : []);
    } catch {
      setDiligencias([]);
    }
  };

  const marcarDiligenciaLida = async (id: string) => {
    try {
      await diligenciaDocumentoService.marcarLida(id);
      setDiligenciasLidas(prev => new Set([...prev, id]));
    } catch {}
  };

  // Carregar documentos institucionais
  const loadDocumentos = async () => {
    if (!currentInstitutionId) {
      setDocumentosInstitucionais([]);
      return;
    }
    try {
      setLoadingDocumentos(true);
      const docs = await documentoInstitucionalService.listar(currentInstitutionId);
      setDocumentosInstitucionais(Array.isArray(docs) ? docs : []);
    } catch (error) {
      console.error('Erro ao carregar documentos institucionais:', error);
      setDocumentosInstitucionais([]);
    } finally {
      setLoadingDocumentos(false);
    }
  };

  const loadDirigenteDocumentosPendentes = async (dirigentesSource?: Dirigente[]) => {
    // Pendencias de documentos sao cobradas apenas de dirigentes com vinculo ativo.
    // "dirigentes" pode conter inativos quando "Mostrar apenas ativos" esta desmarcado
    // (padrao da tela), e sem esse filtro um dirigente ja desligado continuava sendo
    // cobrado por documento pessoal vencido.
    const source = (dirigentesSource || dirigentes || []).filter((dirigente) =>
      Boolean(dirigente?.id || dirigente?.pessoaId)
      && String(dirigente?.statusCargo || '').toLowerCase() === 'ativo'
    );

    if (source.length === 0) {
      setDirigenteDocumentosPendentes([]);
      return;
    }

    try {
      setLoadingDocumentosDirigentes(true);

      const dirigentesPorPessoa = Array.from(
        source.reduce((acc, dirigente) => {
          const dirigenteId = dirigente.pessoaId || dirigente.id;
          if (!dirigenteId) return acc;

          const existente = acc.get(dirigenteId);
          if (existente) {
            existente.push(dirigente);
          } else {
            acc.set(dirigenteId, [dirigente]);
          }
          return acc;
        }, new Map<string, Dirigente[]>()).entries()
      );

      const pendencias = await Promise.all(dirigentesPorPessoa.map(async ([dirigenteId, vinculos]) => {
        if (!dirigenteId) return null;

        const dirigenteBase = vinculos[0];
        const cargos = Array.from(new Set(vinculos.map((item) => item.cargo).filter(Boolean)));
        const cargoPrincipal = cargos.find((cargo) => normalizeCargo(cargo).includes('presidente')) || cargos[0] || '';
        const obrigatorios = Array.from(
          new Map(
            cargos
              .flatMap((cargo) => getTiposDocumentoPessoalObrigatorios(cargo).filter((tipo) => tipo.obrigatorio))
              .map((tipo) => [tipo.tipo, tipo] as const)
          ).values()
        );

        try {
          const documentos = await documentoPessoalService.listarPorDirigente(dirigenteId);
          const documentosAtuais = getDocumentosPessoaisAtuaisPorTipo(Array.isArray(documentos) ? documentos : []);

          const faltantes: string[] = [];
          const enviadosPendentes: string[] = [];
          const reprovados: string[] = [];
          const vencidos: string[] = [];
          const hoje = new Date();
          hoje.setHours(0, 0, 0, 0);

          obrigatorios.forEach((tipo) => {
            const documentoAtual = documentosAtuais.get(tipo.tipo);

            if (!documentoAtual) {
              faltantes.push(tipo.nome);
              return;
            }

            if (documentoAtual.dataValidade
                && new Date(`${documentoAtual.dataValidade.slice(0, 10)}T00:00:00`).getTime() < hoje.getTime()) {
              vencidos.push(tipo.nome);
              return;
            }

            if (isDocumentoReprovado(documentoAtual.statusDocumento)) {
              reprovados.push(tipo.nome);
              return;
            }

            if (!isDocumentoAprovado(documentoAtual.statusDocumento)) {
              enviadosPendentes.push(tipo.nome);
            }
          });

          if (faltantes.length === 0 && enviadosPendentes.length === 0 && reprovados.length === 0 && vencidos.length === 0) {
            return null;
          }

          return {
            dirigenteId,
            nomeCompleto: dirigenteBase.nomeCompleto,
            cargo: cargos.join(', '),
            cargos,
            faltantes,
            enviadosPendentes,
            reprovados,
            vencidos,
          } satisfies DirigenteDocumentosPendencia;
        } catch (error) {
          console.error(`Erro ao carregar documentos pessoais do dirigente ${dirigenteId}:`, error);
          return {
            dirigenteId,
            nomeCompleto: dirigenteBase.nomeCompleto,
            cargo: cargos.join(', '),
            cargos,
            faltantes: obrigatorios.map((tipo) => tipo.nome),
            enviadosPendentes: [],
            reprovados: [],
            vencidos: [],
          } satisfies DirigenteDocumentosPendencia;
        }
      }));

      setDirigenteDocumentosPendentes(
        pendencias.filter((item): item is DirigenteDocumentosPendencia => Boolean(item))
      );
    } finally {
      setLoadingDocumentosDirigentes(false);
    }
  };

  // --- Document upload handlers ---
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedFile(e.target.files?.[0] ?? null);
  };

  const handleTipoDocumentoChange = (value: string) => {
    setTipoDocumentoSelecionado(value as TipoDocumentoInstitucional | '');

    // Try to keep selectedDocConfig in sync so the form shows the correct required fields
    try {
      const configs = Array.isArray(documentConfigs) ? documentConfigs : [] as TipoDocumentoConfig[];
      // First try exact code match
      let found = configs.find(c => String(c.codigo) === String(value));
      if (!found) {
        // Fallback: match by normalized name using the label for the type if available
        const label = TIPOS_DOCUMENTO_LABELS[value as any] || String(value);
        const normalize = (s?: string) => (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
        const target = normalize(label);
        found = configs.find(c => normalize(c.nome).includes(target) || target.includes(normalize(c.nome || '')));
      }

      setSelectedDocConfig(found ?? null);
    } catch (err) {
      console.warn('Erro ao sincronizar selectedDocConfig:', err);
      setSelectedDocConfig(null);
    }
  };

  const handleUploadDocumento = async (e: React.FormEvent) => {
    e.preventDefault();
    const id = editId || savedInstitutionId;
    if (!id) { showToast('Salve a instituição antes de enviar documentos.', 'error'); return; }
    if (!selectedFile) { showToast('Selecione um arquivo.', 'info'); return; }
    if (!tipoDocumentoSelecionado) { showToast('Selecione o tipo de documento.', 'info'); return; }

    // Re-validate file size immediately before upload in case the file was set programmatically
    try {
      if (typeof TAMANHO_MAXIMO_BYTES === 'number' && selectedFile.size > Number(TAMANHO_MAXIMO_BYTES)) {
        showToast(`Arquivo muito grande. Tamanho máximo permitido: ${formatFileSize2(Number(TAMANHO_MAXIMO_BYTES))}`, 'error');
        return;
      }
    } catch (err) {
      console.warn('Erro ao validar tamanho do arquivo antes do upload:', err);
    }
    try {
      setUploadingFile(true);
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('idInstituicao', id);
      formData.append('tipoDocumento', String(tipoDocumentoSelecionado));
      formData.append('descricao', observacoesDoc || '');
      if (numeroDocumento) formData.append('numeroDocumento', numeroDocumento);
      if (dataEmissao) formData.append('dataEmissao', dataEmissao);
      if (dataValidade) formData.append('dataValidade', dataValidade);

      // Se já existe um documento REPROVADO para este tipo, usar substituir (marca o antigo como SUBSTITUIDO)
      const docReprovado = documentosAtuaisPorTipo.get(String(tipoDocumentoSelecionado));
      if (docReprovado && isDocumentoReprovado(docReprovado.statusDocumento) && docReprovado.id) {
        await documentoInstitucionalService.substituir(docReprovado.id, formData);
      } else {
        await documentoInstitucionalService.upload(formData);
      }
      showToast('Documento enviado com sucesso.', 'success');
      setSelectedFile(null);
      if (uploadFileInputRef.current) {
        uploadFileInputRef.current.value = '';
      }
      setTipoDocumentoSelecionado('');
      setNumeroDocumento('');
      setDataEmissao('');
      setDataValidade('');
      setObservacoesDoc('');
      await refreshInstitutionOverview(id);
    } catch (err: any) {
      const status = err?.response?.status;
      // When the browser blocks the response due to CORS or network failures,
      // err.response will be undefined and the error will appear as "Failed to fetch"
      if (status === 413) {
        showToast('Arquivo maior que o permitido pelo servidor. Reduza o tamanho e tente novamente.', 'error');
      } else if (!err?.response) {
        console.error('Upload network/CORS error:', err);
        showToast('Falha de rede ou bloqueio CORS ao enviar o arquivo. Verifique o backend (limite de tamanho / CORS).', 'error');
      } else {
        showToast(err?.response?.data?.error || 'Erro ao enviar documento.', 'error');
      }
    } finally {
      setUploadingFile(false);
    }
  };

  const handleDownloadDocumento = async (id: string, nomeOriginal: string) => {
    try {
      await downloadAuthenticatedFile(documentoInstitucionalService.downloadUrl(id), nomeOriginal);
    } catch {
      setToast({ message: 'Não foi possível baixar o documento.', type: 'error' });
    }
  };

  const handleIniciarAnalise = async () => {
    if (!editId) return;
    setIniciandoAnalise(true);
    try {
      const updated = await institutionService.puxarParaAnalise(editId);
      if (updated?.statusOSC) setStatusOSC(updated.statusOSC);
      showToast('Análise iniciada com sucesso.', 'success');
    } catch (err: any) {
      showToast(err?.response?.data?.error || 'Não foi possível iniciar a análise.', 'error');
    } finally {
      setIniciandoAnalise(false);
    }
  };

  const handleReenviarAnalise = async () => {
    if (!editId) return;
    setReenviandoAnalise(true);
    try {
      const updated = await institutionService.reenviarAnalise(editId);
      if (updated?.statusOSC) setStatusOSC(updated.statusOSC);
      setForm((prev) => ({ ...prev, statusAprovacao: updated?.statusAprovacao, motivoReprovacao: undefined, dataReprovacao: undefined }));
      showToast('Instituição reenviada para análise.', 'success');
    } catch (err: any) {
      showToast(err?.response?.data?.error || 'Não foi possível reenviar para análise.', 'error');
    } finally {
      setReenviandoAnalise(false);
    }
  };

  const handleAprovarInstituicao = async () => {
    if (!editId) return;
    setAprovandoInstituicao(true);
    try {
      const updated = await institutionService.aprovar(editId);
      if (updated?.statusOSC) setStatusOSC(updated.statusOSC);
      setForm((prev) => ({ ...prev, statusAprovacao: updated?.statusAprovacao, motivoReprovacao: undefined, dataReprovacao: undefined }));
      showToast('Instituição aprovada com sucesso.', 'success');
    } catch (err: any) {
      showToast(err?.response?.data?.error || 'Não foi possível aprovar a instituição.', 'error');
    } finally {
      setAprovandoInstituicao(false);
    }
  };

  const handleConfirmarReprovacaoInstituicao = async () => {
    if (!editId || !motivoReprovacao.trim()) return;
    setReprovandoInstituicao(true);
    try {
      const updated = await institutionService.reprovar(editId, motivoReprovacao.trim());
      if (updated?.statusOSC) setStatusOSC(updated.statusOSC);
      setForm((prev) => ({
        ...prev,
        statusAprovacao: updated?.statusAprovacao,
        motivoReprovacao: updated?.motivoReprovacao ?? motivoReprovacao.trim(),
        dataReprovacao: updated?.dataReprovacao,
      }));
      setShowReprovacaoModal(false);
      setMotivoReprovacao('');
      showToast('Instituição reprovada.', 'success');
    } catch (err: any) {
      showToast(err?.response?.data?.error || 'Não foi possível reprovar a instituição.', 'error');
    } finally {
      setReprovandoInstituicao(false);
    }
  };

  const handleConfirmarBloqueioInstituicao = async () => {
    if (!editId || !motivoBloqueio.trim()) return;
    setBloqueando(true);
    try {
      const updated = await institutionService.bloquear(editId, motivoBloqueio.trim());
      setForm((prev) => ({ ...prev, bloqueada: updated?.bloqueada, motivoBloqueio: updated?.motivoBloqueio }));
      setShowBloqueioModal(false);
      setMotivoBloqueio('');
      showToast('Instituição bloqueada.', 'success');
    } catch (err: any) {
      showToast(err?.response?.data?.error || 'Não foi possível bloquear a instituição.', 'error');
    } finally {
      setBloqueando(false);
    }
  };

  const handleDesbloquearInstituicao = async () => {
    if (!editId) return;
    setDesbloqueando(true);
    try {
      const updated = await institutionService.desbloquear(editId);
      setForm((prev) => ({ ...prev, bloqueada: updated?.bloqueada, motivoBloqueio: updated?.motivoBloqueio }));
      showToast('Instituição desbloqueada.', 'success');
    } catch (err: any) {
      showToast(err?.response?.data?.error || 'Não foi possível desbloquear a instituição.', 'error');
    } finally {
      setDesbloqueando(false);
    }
  };

  const handleAprovarDocumento = async (id: string) => {
    if (!confirm('Confirma a aprovação deste documento?')) return;
    try {
      await documentoInstitucionalService.aprovar(id, '');
      showToast('Documento aprovado.', 'success');
      await refreshInstitutionOverview();
    } catch (err: any) {
      showToast('Erro ao aprovar documento.', 'error');
    }
  };

  const handleReprovarDocumento = async (id: string) => {
    const motivo = prompt('Informe o motivo da rejeição:');
    if (!motivo) return;
    try {
      await documentoInstitucionalService.reprovar(id, motivo);
      showToast('Documento reprovado.', 'success');
      await refreshInstitutionOverview();
    } catch (err: any) {
      showToast('Erro ao reprovar documento.', 'error');
    }
  };

  // Deletar documento institucional
  const handleDeleteDocumento = async (id: string) => {
    try {
      if (!confirm('Confirma a exclusão deste documento? Esta ação não pode ser desfeita.')) return;
      await documentoInstitucionalService.deletar(id);
      showToast('Documento excluído com sucesso.', 'success');
      await refreshInstitutionOverview();
    } catch (err: any) {
      console.error('Erro ao excluir documento institucional:', err);
      if (!err?.response) {
        console.error('Delete network/CORS error:', err);
        showToast('Falha de rede ou bloqueio CORS ao excluir documento. Verifique o backend (limite de tamanho / CORS).', 'error');
      } else {
        showToast(err?.response?.data?.error || 'Erro ao excluir documento.', 'error');
      }
    }
  };

  // renderStatusBadge → use shared renderDocStatusBadge from statusOSC.utils

  const handleUploadDeclaracao = async (tipo: TipoDocumentoInstitucional, file: File) => {
    const id = editId || savedInstitutionId;
    if (!id) { showToast("Salve a instituicao antes de enviar documentos.", "error"); return; }
    try {
      setUploadingDeclaracao(true);
      const formData = new FormData();
      formData.append("file", file);
      formData.append("idInstituicao", id);
      formData.append("tipoDocumento", tipo);
      formData.append("assinado", "true");

      // Se já existe uma declaração para este tipo, usar substituir (mantém histórico).
      const docAnterior = documentosAtuaisPorTipo.get(tipo);
      if (shouldSubstituirDocumento(docAnterior)) {
        await documentoInstitucionalService.substituir(docAnterior!.id!, formData);
      } else {
        await documentoInstitucionalService.upload(formData);
      }
      showToast("Declaracao enviada com sucesso.", "success");
      setActiveDeclaracaoUpload(null);
      setSelectedFileDeclaracao(null);
      await refreshInstitutionOverview(id);
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 413) {
        showToast("Arquivo maior que o permitido pelo servidor. Tamanho máximo: 10MB.", "error");
      } else if (!err?.response) {
        showToast("Falha de rede ao enviar o arquivo.", "error");
      } else {
        showToast(err?.response?.data?.error || "Erro ao enviar declaracao.", "error");
      }
    } finally {
      setUploadingDeclaracao(false);
    }
  };

  const renderAlertaVencimento = (dataValidade: string) => {
    const dias = calcularDiasParaVencimento(dataValidade);
    if (dias === null) return null;
    if (dias < 0) return <p className="text-xs text-red-600 mt-0.5">⚠️ Vencido há {Math.abs(dias)} dia(s)</p>;
    if (dias <= 30) return <p className="text-xs text-orange-600 mt-0.5">⚠️ Vence em {dias} dia(s)</p>;
    return null;
  };

  const isDocVencido = (doc: DocInstitucional) => isDocumentoVencido(doc);

  const handleAbrirRenovar = (doc: DocInstitucional) => {
    setRenovarDoc(doc);
    setRenovarFile(null);
    setRenovarDataEmissao('');
    setRenovarDataValidade('');
    setRenovarNumeroDocumento(doc.numeroDocumento || '');
  };

  const handleFecharRenovar = () => {
    setRenovarDoc(null);
    setRenovarFile(null);
    setRenovarDataEmissao('');
    setRenovarDataValidade('');
    setRenovarNumeroDocumento('');
  };

  const handleSubmitRenovar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!renovarDoc?.id || !renovarFile) return;
    const tipoConfig = documentConfigs.find(c => c.codigo === renovarDoc.tipoDocumento);
    if (tipoConfig?.dataEmissaoObrigatoria && !renovarDataEmissao) {
      showToast('Data de emissão é obrigatória para este tipo de documento.', 'error');
      return;
    }
    if (tipoConfig?.dataValidadeObrigatoria && !renovarDataValidade) {
      showToast('Data de validade é obrigatória para este tipo de documento.', 'error');
      return;
    }
    setRenovarLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', renovarFile);
      if (renovarNumeroDocumento) formData.append('numeroDocumento', renovarNumeroDocumento);
      if (renovarDataEmissao) formData.append('dataEmissao', renovarDataEmissao);
      if (renovarDataValidade) formData.append('dataValidade', renovarDataValidade);
      await documentoInstitucionalService.substituir(renovarDoc.id, formData);
      showToast('Documento renovado com sucesso!', 'success');
      handleFecharRenovar();
      await loadDocumentos();
    } catch (err: any) {
      showToast(err?.response?.data?.error || 'Erro ao renovar documento.', 'error');
    } finally {
      setRenovarLoading(false);
    }
  };

  const handleOpenDirigenteModal = (dirigente?: Dirigente) => {
    if (dirigente) {
      setEditingDirigente(dirigente);
      setDirigenteFormData(dirigente);
    } else {
      setEditingDirigente(null);
      setDirigenteFormData({
        instituicaoId: editId || '',
        nomeCompleto: '',
        cpf: '',
        rg: '',
        orgaoExpedidor: '',
        ufOrgaoExpedidor: '',
        dataExpedicao: '',
        dataNascimento: '',
        sexo: '',
        nacionalidade: 'Brasileira',
        estadoCivil: '',
        cargo: '',
        dataInicioCargo: '',
        statusCargo: 'Ativo',
        telefone: '',
        email: '',
        cep: '',
        logradouro: '',
        numero: '',
        bairro: '',
        cidade: '',
        uf: ''
      });
    }
    setShowDirigenteModal(true);
  };

  const handleSaveDirigente = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validar formulÃ¡rio
    const errors = validateDirigenteForm();
    if (Object.keys(errors).length > 0) {
      setDirigenteErrors(errors);

      // Criar lista de campos com erro
      const camposComErro = Object.entries(errors).map(([campo, mensagem]) => {
        const nomeCampo = getNomeCampo(campo);
        return `• ${nomeCampo}: ${mensagem}`;
      }).join('\n');

      showToast('Por favor, corrija os campos obrigatórios antes de enviar.', 'error');

      // Scroll para o primeiro campo com erro
      setTimeout(() => {
        const firstErrorField = document.querySelector('.border-red-500') as HTMLElement;
        if (firstErrorField) {
          firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
          firstErrorField.focus();
        }
      }, 100);

      return;
    }

    try {
      if (editingDirigente) {
        await dirigenteService.atualizar(editingDirigente.id!, dirigenteFormData);
        showToast('Dirigente atualizado com sucesso!', 'success');
      } else {
       // Antes de criar, verificar se jÃ¡ existe dirigente com mesmo CPF
       try {
         const cpfLimpo = (dirigenteFormData.cpf || '').replace(/\D/g, '');
         if (cpfLimpo.length === 11) {
           const existente = await dirigenteService.buscarPorCpf(cpfLimpo);
           if (existente && existente.id) {
             // Perguntar ao usuÃ¡rio se prefere apenas vincular em vez de criar duplicata
              const confirmMsg = `Já existe um dirigente cadastrado com o CPF ${formatCPF(cpfLimpo)}: ${existente.nomeCompleto}. Deseja apenas vincular essa pessoa ao cargo "${dirigenteFormData.cargo}" nesta instituição?`;
             if (window.confirm(confirmMsg)) {
               // Criar vÃ­nculo usando endpoint especÃ­fico
               const instituicaoId = editId || dirigenteFormData.instituicaoId || '';
               if (!instituicaoId) {
                  showToast('Não foi possível vincular: instituição não identificada.', 'error');
               } else {
                 await dirigenteService.criarVinculo(existente.id, {
                   instituicaoId,
                   cargo: dirigenteFormData.cargo,
                   dataInicioCargo: dirigenteFormData.dataInicioCargo,
                   statusCargo: dirigenteFormData.statusCargo,
                 });
                  showToast('Vínculo criado com sucesso!', 'success');
                 setShowDirigenteModal(false);
                 setDirigenteErrors({});
                 await refreshInstitutionOverview(instituicaoId);
                 return; // nÃ£o continuar com criaÃ§Ã£o
               }
             }
           }
         }
       } catch (err) {
         console.warn('Erro ao verificar dirigente por CPF antes de criar:', err);
         // Se falhar na busca por CPF, seguir com o fluxo normal de criaÃ§Ã£o
       }
        await dirigenteService.criar(dirigenteFormData);
        showToast('Dirigente cadastrado com sucesso!', 'success');
      }

      setShowDirigenteModal(false);
      setDirigenteErrors({});
      await refreshInstitutionOverview();
    } catch (error: any) {
      console.error('Erro ao salvar dirigente:', error);
      const errorMessage = error?.response?.data?.error || 'Erro ao salvar dirigente';
      const duplicateActiveLink = typeof errorMessage === 'string' && errorMessage.toLowerCase().includes('vÃ­nculo ativo');

      if (duplicateActiveLink) {
        showToast('Este vínculo já existe. Atualizando a lista da diretoria...', 'info');
        setShowDirigenteModal(false);
        setDirigenteErrors({});
        setActiveTab('dirigentes');
        await refreshInstitutionOverview();
        return;
      }

      showToast(errorMessage, 'error');
    }
  };

  // ValidaÃ§Ã£o do formulÃ¡rio de dirigente
  const getNomeCampo = (campo: string): string => {
    const nomes: Record<string, string> = {
      nomeCompleto: 'Nome Completo',
      cpf: 'CPF',
      rg: 'RG',
      orgaoExpedidor: 'Órgão Expedidor',
      ufOrgaoExpedidor: 'UF do Órgão',
      dataExpedicao: 'Data de Expedição',
      dataNascimento: 'Data de Nascimento',
      sexo: 'Sexo',
      estadoCivil: 'Estado Civil',
      cargo: 'Cargo',
      dataInicioCargo: 'Data de Início do Cargo',
      email: 'E-mail',
      telefone: 'Telefone',
      cep: 'CEP',
      logradouro: 'Logradouro',
      numero: 'Número',
      bairro: 'Bairro',
      cidade: 'Cidade',
      uf: 'UF'
    };
    return nomes[campo] || campo;
  };

  const validateDirigenteForm = (): Record<string, string> => {
    const errors: Record<string, string> = {};

    // Nome completo
    if (!dirigenteFormData.nomeCompleto || dirigenteFormData.nomeCompleto.trim().length < 3) {
      errors.nomeCompleto = 'Nome completo deve ter no mínimo 3 caracteres';
    }

    // CPF
    if (!dirigenteFormData.cpf || dirigenteFormData.cpf.replace(/\D/g, '').length !== 11) {
      errors.cpf = 'CPF inválido (deve ter 11 dígitos)';
    }

    // Ã“rgÃ£o expedidor
    if (!dirigenteFormData.orgaoExpedidor || dirigenteFormData.orgaoExpedidor.trim().length < 2) {
      errors.orgaoExpedidor = 'Órgão expedidor inválido';
    }

    // UF do Ã³rgÃ£o
    if (!dirigenteFormData.ufOrgaoExpedidor) {
      errors.ufOrgaoExpedidor = 'Selecione a UF';
    }

    // Data de expediÃ§Ã£o
    if (!dirigenteFormData.dataExpedicao) {
      errors.dataExpedicao = 'Data de expedição obrigatória';
    } else if (new Date(dirigenteFormData.dataExpedicao) > new Date()) {
      errors.dataExpedicao = 'Data não pode ser futura';
    }

    // Data de nascimento
    if (!dirigenteFormData.dataNascimento) {
      errors.dataNascimento = 'Data de nascimento obrigatÃ³ria';
    } else {
      const idade = new Date().getFullYear() - new Date(dirigenteFormData.dataNascimento).getFullYear();
      if (idade < 18) {
        errors.dataNascimento = 'Dirigente deve ter no mínimo 18 anos';
      }
      if (idade > 120) {
        errors.dataNascimento = 'Data de nascimento inválida';
      }
    }

    // Sexo
    if (!dirigenteFormData.sexo) {
      errors.sexo = 'Selecione o sexo';
    }

    // Estado civil
    if (!dirigenteFormData.estadoCivil) {
      errors.estadoCivil = 'Selecione o estado civil';
    }

    // Cargo
    if (!dirigenteFormData.cargo) {
      errors.cargo = 'Selecione o cargo';
    }

    // Data inÃ­cio cargo
    if (!dirigenteFormData.dataInicioCargo) {
      errors.dataInicioCargo = 'Data de início obrigatória';
    }

    // Email
    if (!dirigenteFormData.email || !isValidEmail(dirigenteFormData.email)) {
      errors.email = 'Email inválido';
    }

    // Telefone
    if (!dirigenteFormData.telefone || dirigenteFormData.telefone.replace(/\D/g, '').length < 10) {
      errors.telefone = 'Telefone inválido (mínimo 10 dígitos)';
    }

    // CEP
    if (!dirigenteFormData.cep || dirigenteFormData.cep.replace(/\D/g, '').length !== 8) {
      errors.cep = 'CEP inválido (deve ter 8 dígitos)';
    }

    // Logradouro
    if (!dirigenteFormData.logradouro || dirigenteFormData.logradouro.trim().length < 3) {
      errors.logradouro = 'Logradouro inválido';
    }

    // NÃºmero
    if (!dirigenteFormData.numero || dirigenteFormData.numero.trim().length === 0) {
      errors.numero = 'Número obrigatório';
    }

    // Bairro
    if (!dirigenteFormData.bairro || dirigenteFormData.bairro.trim().length < 2) {
      errors.bairro = 'Bairro inválido';
    }

    // Cidade
    if (!dirigenteFormData.cidade || dirigenteFormData.cidade.trim().length < 2) {
      errors.cidade = 'Cidade inválida';
    }

    // UF
    if (!dirigenteFormData.uf) {
      errors.uf = 'Selecione a UF';
    }

    return errors;
  };

  // Buscar CEP para dirigente
    const handleBuscarCepDirigente = async () => {
    const cepLimpo = dirigenteFormData.cep.replace(/\D/g, '');

    if (cepLimpo.length !== 8) {
      showToast('CEP inválido. Digite 8 dígitos.', 'error');
      return;
    }

    try {
      setLoadingCepDirigente(true);
      const endereco = await lookupCep(cepLimpo);

      setDirigenteFormData({
        ...dirigenteFormData,
        logradouro: endereco.logradouro || '',
        bairro: endereco.bairro || '',
        cidade: endereco.localidade || '',
        uf: endereco.uf || ''
      });

      // Limpar erros de endereÃ§o
      const newErrors = { ...dirigenteErrors };
      delete newErrors.cep;
      delete newErrors.logradouro;
      delete newErrors.bairro;
      delete newErrors.cidade;
      delete newErrors.uf;
      setDirigenteErrors(newErrors);

      showToast('Endereço preenchido com sucesso!', 'success');
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
      showToast('CEP não encontrado. Preencha manualmente.', 'error');
    } finally {
      setLoadingCepDirigente(false);
    }
    };

    // Buscar CEP para o formulÃ¡rio de instituiÃ§Ã£o (CNPJ/instituiÃ§Ã£o)
    const handleCepBlur = async () => {
    const cepLimpo = (form.cep ?? '').replace(/\D/g, '');

    if (cepLimpo.length !== 8) {
      showToast('CEP inválido. Digite 8 dígitos.', 'error');
      return;
    }

    try {
      setLoadingCep(true);
      const endereco = await lookupCep(cepLimpo);

      setForm((prev) => ({
        ...prev,
        logradouro: endereco.logradouro || '',
        bairro: endereco.bairro || '',
        cidade: endereco.localidade || '',
        uf: endereco.uf || prev.uf || 'MT',
      }));

      // Clear any cep-related toast if present and show success
      showToast('Endereço preenchido com sucesso!', 'success');
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
      showToast('CEP não encontrado. Preencha manualmente.', 'error');
    } finally {
      setLoadingCep(false);
    }
    };

  // FunÃ§Ãµes para modal de documentos pessoais do dirigente
  const handleOpenDocumentosPessoais = (dirigente: Dirigente) => {
    setDirigenteDocumentosPessoais(dirigente);
    setShowDocumentosPessoaisModal(true);
  };

  const handleCloseDocumentosPessoais = () => {
    setShowDocumentosPessoaisModal(false);
    setDirigenteDocumentosPessoais(null);
  };

  const formatCPF = (value: string) => {
    return value
      .replace(/\D/g, '')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  };

  const formatCEPDirigente = (value: string) => {
    return value
      .replace(/\D/g, '')
      .replace(/(\d{5})(\d)/, '$1-$2');
  };

  const formatPhone = (value: string) => {
    return value
      .replace(/\D/g, '')
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{5})(\d)/, '$1-$2');
  };

  // Carregar dirigentes, documentos e planos imediatamente (eager load) quando editId estiver disponÃ­vel
  useEffect(() => {
    if (currentInstitutionId) {
      loadDirigentes();
      loadDocumentos();
      refreshInstitutionStatus(currentInstitutionId);
    }
  }, [currentInstitutionId, apenasAtivos]);

  // Create a normalized snapshot of the form for dirty checks
  const makeSnapshot = (f: FormState) => {
    const copy: any = { ...f };
    // normalize undefined -> '' for stable comparison
    Object.keys(copy).forEach(k => {
      if (copy[k] === undefined || copy[k] === null) copy[k] = '';
    });
    // ensure arrays sorted
    if (Array.isArray(copy.areasAtuacao)) copy.areasAtuacao = [...copy.areasAtuacao].sort();
    return JSON.stringify(copy);
  };

  const isDirty = useMemo(() => {
    const snap = makeSnapshot(form);
    return snap !== loadedSnapshot;
  }, [form, loadedSnapshot]);

  // Group documents into obrigatorios/opcionais for UI
  const documentosPorCategoria = useMemo(() => {
    const docs = documentosVisiveis;
    const obrigatorios = docs.filter(d => DOCUMENTOS_OBRIGATORIOS.includes(d.tipoDocumento));
    const opcionais = docs.filter(d => !DOCUMENTOS_OBRIGATORIOS.includes(d.tipoDocumento));
    return { obrigatorios, opcionais };
  }, [documentosVisiveis]);

  // If an id is provided, load the record to edit and prefill the form.
  useEffect(() => {
    if (!editId) return;

    let cancelled = false;
    (async () => {
      setLoadingExisting(true);
      setError(null);
      setSuccess(null);
      try {
        const existing = await institutionService.getById(editId);
        if (cancelled) return;

        console.log('ðŸ”„ CARREGANDO DADOS DA INSTITUIÃ‡ÃƒO');
        console.log('editId:', editId);
        console.log('existing.institutionId:', existing.institutionId);
        console.log('existing completo:', existing);

        setForm((prev) => ({
          ...prev,
          ...existing,
          // Ensure arrays are always arrays
          areasAtuacao: Array.isArray(existing.areasAtuacao) ? existing.areasAtuacao : [],
        }));

        // Set snapshot to current loaded entity (no unsaved changes)
        setLoadedSnapshot(makeSnapshot({
          ...existing,
          areasAtuacao: Array.isArray(existing.areasAtuacao) ? existing.areasAtuacao : [],
        }));

        // Remember that this entity exists on the server
        if (existing.institutionId) setSavedInstitutionId(existing.institutionId);

        // Sync statusOSC from loaded institution
        if (existing.statusOSC) setStatusOSC(existing.statusOSC);
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.response?.data?.error || 'Não foi possível carregar a instituição para edição.');
      } finally {
        if (!cancelled) setLoadingExisting(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [editId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (hasErrors) {
      setError('Revise os campos destacados.');
      return;
    }

    setSaving(true);
    try {
      const payload: Partial<InstitutionDTO> = {
         razaoSocial: (form.razaoSocial ?? '').trim(),
         nomeFantasia: (form.nomeFantasia ?? '').trim() || undefined,
         cnpj: onlyDigits(form.cnpj ?? ''),
         inscricaoEstadual: (form.inscricaoEstadual ?? '').trim() || undefined,
         inscricaoMunicipal: (form.inscricaoMunicipal ?? '').trim(),
         dataFundacao: (form.dataFundacao ?? '').trim() || undefined,
         areasAtuacao: form.areasAtuacao ?? [],

         telefone: (form.telefone ?? '').trim(),
         celular: (form.celular ?? '').trim() || undefined,
         emailInstitucional: (form.emailInstitucional ?? '').trim(),
         emailSecundario: (form.emailSecundario ?? '').trim() || undefined,
         website: (form.website ?? '').trim() || undefined,

         cep: onlyDigits(form.cep ?? ''),
         logradouro: (form.logradouro ?? '').trim(),
         numero: (form.numero ?? '').trim(),
         complemento: (form.complemento ?? '').trim() || undefined,
         bairro: (form.bairro ?? '').trim(),
         cidade: (form.cidade ?? '').trim(),
         uf: (form.uf ?? '').trim(),
         pontoReferencia: (form.pontoReferencia ?? '').trim() || undefined,

         numeroRegistroConselhoMunicipal: (form.numeroRegistroConselhoMunicipal ?? '').trim(),
         dataRegistroConselho: (form.dataRegistroConselho ?? '').trim() || undefined,
         objetoSocial: (form.objetoSocial ?? '').trim() || undefined,
         quantidadeBeneficiarios: form.quantidadeBeneficiarios != null ? Number(form.quantidadeBeneficiarios) : undefined,
      };

      // IMPORTANT: Determine if this is an UPDATE or CREATE
      // Check BOTH editId (from URL) AND form.institutionId (from loaded data)
      // This prevents duplicate email errors when data was loaded from CNPJ search
      const institutionIdToUpdate = editId || form.institutionId;
      const isEdit = Boolean(institutionIdToUpdate && institutionIdToUpdate.trim().length > 0);

      console.log('=====================================');
      console.log('ðŸ” SUBMIT DEBUG - ANTES DE ENVIAR');
      console.log('=====================================');
      console.log('editId (da URL):', editId);
      console.log('form.institutionId:', form.institutionId);
      console.log('institutionIdToUpdate:', institutionIdToUpdate);
      console.log('isEdit:', isEdit);
      console.log('CNPJ:', payload.cnpj);
      console.log('Email:', payload.emailInstitucional);
      console.log('MÃ©todo que serÃ¡ usado:', isEdit ? 'PUT (UPDATE)' : 'POST (CREATE)');
      console.log('URL que serÃ¡ chamada:', isEdit ? `/api/institutions/${institutionIdToUpdate}` : '/api/institutions');
      console.log('Payload completo:', JSON.stringify(payload, null, 2));
      console.log('=====================================');

      if (isEdit) {
        await institutionService.update(institutionIdToUpdate as string, payload);

        // IMPORTANTE: Vincular usuÃ¡rio Ã  instituiÃ§Ã£o apÃ³s update
        // Isso Ã© necessÃ¡rio quando o usuÃ¡rio busca por CNPJ e edita uma instituiÃ§Ã£o existente
        try {
          console.log('ðŸ”— Vinculando usuÃ¡rio Ã  instituiÃ§Ã£o apÃ³s UPDATE...');
          await getCurrentUserData();
          refreshUser();
          console.log('âœ… Dados do usuÃ¡rio atualizados');
        } catch (err) {
          console.warn('âš ï¸ Erro ao atualizar dados do usuÃ¡rio apÃ³s UPDATE:', err);
          // NÃ£o bloqueia o fluxo - vinculaÃ§Ã£o pode jÃ¡ existir
        }

        showToast('Cadastro atualizado com sucesso.', 'success');
        // Stay on page, exit edit mode
        setIsEditMode(false);
        setLoadedSnapshot(makeSnapshot(payload));
        setSavedInstitutionId(institutionIdToUpdate as string);
        await refreshInstitutionStatus(institutionIdToUpdate as string);
      } else {
        const created = await institutionService.create(payload);
        showToast('Cadastro realizado com sucesso e vinculado ao seu usuário.', 'success');

        // Remember saved id and snapshot
        const newId = created.institutionId || created.id || null;
        if (newId) {
          setSavedInstitutionId(newId);
          setLoadedSnapshot(makeSnapshot({ ...payload, institutionId: newId }));
        }

        // Buscar dados atualizados do usuÃ¡rio
        try {
          await getCurrentUserData();
          refreshUser();
        } catch (err) {
          console.error('Erro ao atualizar dados do usuÃ¡rio:', err);
        }

        // For all profiles: update URL to edit mode and remain on the cadastro page after create
        if (newId) {
          navigate(`/dashboard/cadastro-dados-institucionais?id=${encodeURIComponent(newId)}`);
        }
      }
    } catch (err: any) {
      console.error('[CadastroDadosInstitucionais] submit error', err);
      setError(err?.response?.data?.error || 'Erro ao cadastrar dados institucionais.');
    } finally {
      setSaving(false);
    }
  };

  // Tab switching â€” free, no unsaved-changes block
  const handleTabChange = (tab: "instituicao" | "dirigentes" | "documentos" | "declaracoes" | "planos") => {
    if (tab === "instituicao") { setActiveTab(tab); return; }
    const hasId = Boolean(editId || savedInstitutionId);
    if (!hasId) {
      showToast('Por favor, salve a instituição antes de acessar as outras abas.', 'info');
      return;
    }

    setActiveTab(tab);
  };

  const isRequiredInstitutionFieldMissing = (key: keyof FormState) => {
    const config = REQUIRED_INSTITUTION_FIELDS.find((field) => field.key === key);
    if (!config) return false;
    const value = form[key as keyof InstitutionDTO];
    if (config.validate) return !config.validate(value);
    return String(value ?? '').trim().length === 0;
  };

  const isRequiredInstitutionField = (key: keyof FormState) =>
    REQUIRED_INSTITUTION_FIELDS.some((field) => field.key === key);

  const fieldClass = (key: keyof FormState) =>
    `${inputBaseClass} ${
      errors[String(key)]
        ? 'border-red-400 bg-red-50 dark:border-red-500/70 dark:bg-red-950/20'
        : isEditMode && isRequiredInstitutionFieldMissing(key)
          ? 'border-orange-400 bg-orange-50 dark:border-orange-500/70 dark:bg-orange-950/20'
          : isEditMode && isRequiredInstitutionField(key)
            ? 'border-orange-300 dark:border-orange-500/60'
          : ''
    }`;

  const help = (key: keyof FormState) =>
    errors[String(key)] ? (
      <p className="mt-1 text-xs text-red-600 dark:text-red-300">{errors[String(key)]}</p>
    ) : isEditMode && isRequiredInstitutionFieldMissing(key) ? (
      <p className="mt-1 text-xs font-medium text-orange-700 dark:text-orange-300">Preenchimento obrigatório para aprovação.</p>
    ) : null;

  const labelClass = (key: keyof FormState) =>
    `mb-1 block text-sm font-medium ${
      isEditMode && isRequiredInstitutionFieldMissing(key) ? 'text-orange-800 dark:text-orange-300' : 'text-slate-700 dark:text-slate-200'
    }`;

  const getMissingFieldsForSection = (sectionTitle: string) => {
    const section = INSTITUTION_FORM_SECTIONS.find((item) => item.title === sectionTitle);
    if (!section) return [];

    return section.keys
      .filter((key) => isRequiredInstitutionFieldMissing(key))
      .map((key) => REQUIRED_INSTITUTION_FIELDS.find((field) => field.key === key)?.label || String(key));
  };

  const getTabSeverityClasses = (severity: ComplianceSeverity, active: boolean) => {
    const base = 'inline-flex rounded-lg border px-3 py-2 text-sm font-medium transition-colors';
    if (severity === 'danger') {
      return active
        ? `${base} border-red-300 bg-red-50 text-red-800 dark:border-red-500/50 dark:bg-red-950/30 dark:text-red-200`
        : `${base} border-transparent text-red-700 hover:border-red-200 hover:bg-red-50 dark:text-red-300 dark:hover:border-red-500/40 dark:hover:bg-red-950/20`;
    }
    if (severity === 'warning') {
      return active
        ? `${base} border-orange-300 bg-orange-50 text-orange-800 dark:border-orange-500/50 dark:bg-orange-950/30 dark:text-orange-200`
        : `${base} border-transparent text-orange-700 hover:border-orange-200 hover:bg-orange-50 dark:text-orange-300 dark:hover:border-orange-500/40 dark:hover:bg-orange-950/20`;
    }
    return active
      ? `${base} ${isSaas ? 'border-teal-300 bg-teal-50 text-teal-800 dark:border-teal-500/50 dark:bg-teal-950/30 dark:text-teal-200' : 'border-blue-300 bg-blue-50 text-blue-800 dark:border-blue-500/50 dark:bg-blue-950/30 dark:text-blue-200'}`
        : `${base} border-transparent text-slate-700 hover:border-slate-200 hover:bg-slate-50 dark:text-slate-300 dark:hover:border-slate-700 dark:hover:bg-slate-900`;
  };

  const cadastroConcluido = cadastroObrigatorioFaltantes.length === 0;
  const diretoriaConcluida = dirigentesObrigatoriosFaltantes.length === 0 && totalPendenciasDirigentes === 0;
  const documentosConcluidos = dashboard.obrigatoriosFaltando === 0
    && dashboard.reprovados === 0
    && dashboard.vencidos === 0
    && dashboard.pendentes === 0;
  const podeVerBotaoEditar = hasPermission(Permission.EDIT_INSTITUTION);
  const edicaoBloqueadaPorStatus = [StatusOSC.EM_ANALISE, StatusOSC.APROVADO, StatusOSC.SUSPENSA, StatusOSC.INATIVA].includes(statusOSC);
  const decisaoConcluida = statusOSC === StatusOSC.APROVADO || statusOSC === StatusOSC.REPROVADO;
  const etapaAnaliseAtual = !cadastroConcluido
    ? 0
    : !diretoriaConcluida
      ? 1
      : !documentosConcluidos
        ? 2
        : 3;
  const etapasAnalise = [
    {
      label: 'Cadastro',
      detalhe: cadastroConcluido ? 'Dados completos' : `${cadastroObrigatorioFaltantes.length} pendência(s)`,
      concluida: cadastroConcluido,
    },
    {
      label: 'Diretoria',
      detalhe: diretoriaConcluida ? 'Regular' : dirigentesObrigatoriosFaltantes.length > 0
        ? `Falta ${dirigentesObrigatoriosFaltantes.join(' e ')}`
        : `${totalPendenciasDirigentes} pendência(s)`,
      concluida: diretoriaConcluida,
    },
    {
      label: 'Documentos',
      detalhe: documentosConcluidos
        ? 'Documentação aprovada'
        : `${dashboardDocsTab.obrigatoriosEnviados}/${dashboardDocsTab.obrigatoriosTotal} enviados`,
      alerta: dashboard.vencidos > 0
        ? `${dashboard.vencidos} vencido${dashboard.vencidos > 1 ? 's' : ''}`
        : null,
      concluida: documentosConcluidos,
    },
    {
      label: 'Decisão',
      detalhe: decisaoConcluida
        ? statusOSC === StatusOSC.APROVADO ? 'Aprovada' : 'Reprovada'
        : statusOSC === StatusOSC.EM_ANALISE ? 'Em análise' : 'Aguardando requisitos',
      concluida: decisaoConcluida,
    },
  ];

  return (
    <div className="w-full space-y-6">
      <header className={headerClass} aria-label="Identificação da instituição">
        <div className="min-w-0">
          <p className={`text-xs font-semibold uppercase tracking-[0.18em] ${accentTextClass}`}>
            {editId ? 'Cadastro institucional' : 'Nova instituição'}
          </p>
          <h1 className="mt-1 truncate text-2xl font-bold tracking-tight text-slate-950 dark:text-white">
            {editId ? form.razaoSocial || 'Instituição' : 'Cadastrar instituição'}
          </h1>
          <div className="mt-2 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-500 dark:text-slate-400">
              {form.cnpj && <span>CNPJ {formatCnpj(form.cnpj)}</span>}
              <span>Cadastro, diretoria e documentação</span>
            </div>
            {editId && (
              <div className="flex shrink-0 flex-wrap items-center gap-2">
                {activeTab === 'instituicao' && currentInstitutionId && (
                  <button
                    type="button"
                    onClick={() => openAuthenticatedTab(`${BASENAME}/instituicao/impressao/${encodeURIComponent(currentInstitutionId)}?autoprint=1`)}
                    className={`${secondaryButtonClass} px-3 py-2`}
                  >
                    Imprimir conformidade
                  </button>
                )}
                {activeTab === 'instituicao' && (
                  isEditMode ? (
                    <>
                      <button type="button" onClick={handleCancel} className={`${secondaryButtonClass} px-3 py-2`}>
                        Cancelar
                      </button>
                      <button type="submit" form="form-instituicao" disabled={saving} className={`${primaryButtonClass} px-3 py-2`}>
                        {saving ? 'Salvando...' : 'Salvar alterações'}
                      </button>
                    </>
                  ) : podeVerBotaoEditar && (
                    <button
                      type="button"
                      onClick={() => setIsEditMode(true)}
                      disabled={edicaoBloqueadaPorStatus}
                      title={edicaoBloqueadaPorStatus ? 'Cadastro bloqueado para edição no status atual da instituição' : undefined}
                      className="inline-flex items-center justify-center rounded-lg bg-amber-500 px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-amber-600 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-amber-500"
                    >
                      Editar
                    </button>
                  )
                )}
                <StatusOSCBadge status={effectiveStatusOSC} size="md" showDescription={false} />
              </div>
            )}
          </div>
        </div>
      </header>

      {editId && (
        <section className={`${panelClass} p-5`} aria-label="Tramitação da análise institucional">
          <div className="mb-5 flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between">
            <h2 className="text-sm font-semibold text-slate-950 dark:text-white">Tramitação da análise</h2>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Etapa {etapaAnaliseAtual + 1} de {etapasAnalise.length} · <strong className="font-semibold text-amber-700 dark:text-amber-300">{etapasAnalise[etapaAnaliseAtual].label}</strong>
            </p>
          </div>
          <ol className="grid grid-cols-4">
            {etapasAnalise.map((etapa, index) => {
              const ativa = index === etapaAnaliseAtual && !etapa.concluida;
              return (
                <li key={etapa.label} className="relative flex min-w-0 flex-col items-center text-center">
                  {index > 0 && (
                    <span
                      className={`absolute top-[11px] h-0.5 w-full -translate-x-1/2 ${etapa.concluida ? isSaas ? 'bg-teal-500' : 'bg-blue-600' : 'bg-slate-200 dark:bg-slate-700'}`}
                    />
                  )}
                  <span className={`relative z-10 flex h-6 w-6 items-center justify-center rounded-full border-2 text-[11px] font-bold ${
                    etapa.concluida
                      ? isSaas ? 'border-teal-600 bg-teal-600 text-white' : 'border-blue-700 bg-blue-700 text-white'
                      : ativa
                        ? 'scale-110 border-amber-500 bg-amber-500 text-white ring-4 ring-amber-200/70 dark:ring-amber-500/20'
                        : 'border-slate-300 bg-white text-slate-400 dark:border-slate-600 dark:bg-slate-900'
                  }`}>
                    {etapa.concluida ? '✓' : index + 1}
                  </span>
                  <span className={`mt-2 text-xs font-semibold ${ativa ? 'text-amber-700 dark:text-amber-300' : etapa.concluida ? 'text-slate-700 dark:text-slate-200' : 'text-slate-400 dark:text-slate-500'}`}>
                    {etapa.label}
                  </span>
                  {'alerta' in etapa && etapa.alerta ? (
                    <span className="mt-0.5 hidden rounded-full bg-red-50 px-2 py-0.5 text-[10px] font-semibold text-red-700 dark:bg-red-950/40 dark:text-red-300 sm:inline-flex">
                      {etapa.alerta}
                    </span>
                  ) : (
                    <span className="mt-0.5 hidden max-w-[9rem] truncate text-[11px] text-slate-500 dark:text-slate-400 sm:block" title={etapa.detalhe}>
                      {etapa.detalhe}
                    </span>
                  )}
                </li>
              );
            })}
          </ol>
        </section>
      )}

      {editId && (
        <section className={`${panelClass} overflow-hidden`} aria-label="Próxima ação da análise">
          <div className="grid lg:grid-cols-[minmax(0,1fr)_auto]">
            <div className="p-5">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">Próximo passo</p>
              {statusOSC === StatusOSC.EM_ANALISE && canAprovarReprovarInstituicao ? (
                <>
                  <h2 className="mt-1 text-lg font-semibold text-slate-950 dark:text-white">Registrar decisão da análise</h2>
                  <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                    Revise os dados e documentos antes de aprovar ou devolver a instituição.
                  </p>
                  {(totalPendenciasObrigatorias > 0 || totalPendenciasAnalise > 0) && (
                    <p className="mt-3 text-sm font-medium text-amber-700 dark:text-amber-300">
                      A aprovação permanece indisponível enquanto houver pendências.
                    </p>
                  )}
                  <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                    <button
                      type="button"
                      onClick={handleAprovarInstituicao}
                      disabled={aprovandoInstituicao || reprovandoInstituicao || totalPendenciasObrigatorias > 0 || totalPendenciasAnalise > 0}
                      className="inline-flex items-center justify-center rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-45"
                    >
                      {aprovandoInstituicao ? 'Aprovando...' : 'Aprovar instituição'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowReprovacaoModal(true)}
                      disabled={aprovandoInstituicao || reprovandoInstituicao}
                      className={secondaryButtonClass}
                    >
                      Reprovar com justificativa
                    </button>
                  </div>
                </>
              ) : statusOSC === StatusOSC.AGUARDANDO_ANALISE && canIniciarAnalise ? (
                <>
                  <h2 className="mt-1 text-lg font-semibold text-slate-950 dark:text-white">Cadastro pronto para conferência</h2>
                  <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">Assuma a análise para registrar a decisão institucional.</p>
                  <button type="button" onClick={handleIniciarAnalise} disabled={iniciandoAnalise} className={`${primaryButtonClass} mt-4`}>
                    {iniciandoAnalise ? 'Iniciando...' : 'Iniciar análise'}
                  </button>
                </>
              ) : statusOSC === StatusOSC.AGUARDANDO_ANALISE || statusOSC === StatusOSC.EM_ANALISE ? (
                <>
                  <h2 className="mt-1 text-lg font-semibold text-slate-950 dark:text-white">
                    {statusOSC === StatusOSC.EM_ANALISE ? 'Instituição em análise' : 'Aguardando análise'}
                  </h2>
                  <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                    {statusOSC === StatusOSC.EM_ANALISE
                      ? 'A equipe da Prefeitura está analisando o cadastro. Você será avisado da decisão.'
                      : 'O cadastro foi enviado e está na fila da Prefeitura. Não há mais nada a fazer por enquanto.'}
                  </p>
                </>
              ) : statusOSC === StatusOSC.REPROVADO ? (
                <>
                  <h2 className="mt-1 text-lg font-semibold text-red-700 dark:text-red-300">Instituição reprovada</h2>
                  <div className="mt-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-900 dark:border-red-500/40 dark:bg-red-950/30 dark:text-red-200">
                    <p className="font-semibold">Justificativa da reprovação</p>
                    <p className="mt-1 whitespace-pre-wrap">{form.motivoReprovacao || 'Motivo não informado.'}</p>
                  </div>
                  <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
                    Ajuste o cadastro, a diretoria e os documentos apontados e depois peça nova análise.
                  </p>
                  {(totalPendenciasObrigatorias > 0 || totalPendenciasAnalise > 0) && (
                    <p className="mt-2 text-sm font-medium text-amber-700 dark:text-amber-300">
                      O reenvio fica indisponível enquanto houver pendências.
                    </p>
                  )}
                  {canAprovarReprovarInstituicao && (
                    <button
                      type="button"
                      onClick={handleAprovarInstituicao}
                      disabled={aprovandoInstituicao || totalPendenciasObrigatorias > 0 || totalPendenciasAnalise > 0}
                      className="mr-2 mt-4 inline-flex items-center justify-center rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-45"
                    >
                      {aprovandoInstituicao ? 'Aprovando...' : 'Aprovar após ajustes'}
                    </button>
                  )}
                  {hasPermission(Permission.EDIT_INSTITUTION) && (
                    <button
                      type="button"
                      onClick={handleReenviarAnalise}
                      disabled={reenviandoAnalise || totalPendenciasObrigatorias > 0 || totalPendenciasAnalise > 0}
                      className={`${primaryButtonClass} mt-4 disabled:cursor-not-allowed disabled:opacity-45`}
                    >
                      {reenviandoAnalise ? 'Reenviando...' : 'Reenviar para análise'}
                    </button>
                  )}
                </>
              ) : statusOSC === StatusOSC.APROVADO && canAprovarReprovarInstituicao ? (
                <>
                  <h2 className="mt-1 text-lg font-semibold text-slate-950 dark:text-white">Instituição aprovada</h2>
                  <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                    Se algum dado ou documento precisar de correção, devolva a instituição com justificativa.
                  </p>
                  <button
                    type="button"
                    onClick={() => setShowReprovacaoModal(true)}
                    disabled={aprovandoInstituicao || reprovandoInstituicao}
                    className={`${secondaryButtonClass} mt-4`}
                  >
                    Reprovar para correção
                  </button>
                </>
              ) : decisaoConcluida ? (
                <>
                  <h2 className="mt-1 text-lg font-semibold text-slate-950 dark:text-white">Análise concluída</h2>
                  <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">A decisão institucional já foi registrada.</p>
                </>
              ) : (
                <>
                  <h2 className="mt-1 text-lg font-semibold text-slate-950 dark:text-white">Regularizar requisitos da instituição</h2>
                  <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                    {etapasAnalise[etapaAnaliseAtual].detalhe}. Conclua esta etapa para avançar na análise.
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      if (!cadastroConcluido) {
                        if (!edicaoBloqueadaPorStatus) setIsEditMode(true);
                      } else if (!diretoriaConcluida) handleTabChange('dirigentes');
                      else handleTabChange('documentos');
                    }}
                    disabled={!cadastroConcluido && edicaoBloqueadaPorStatus}
                    title={!cadastroConcluido && edicaoBloqueadaPorStatus ? 'Cadastro bloqueado para edição no status atual da instituição' : undefined}
                    className={`${primaryButtonClass} mt-4 disabled:cursor-not-allowed disabled:opacity-50`}
                  >
                    {!cadastroConcluido ? 'Editar cadastro' : !diretoriaConcluida ? 'Abrir diretoria' : 'Revisar documentos'}
                  </button>
                </>
              )}
            </div>

            {hasPermission(Permission.BLOCK_INSTITUTION) && (
              <aside className="border-t border-slate-200 bg-slate-50/70 p-5 dark:border-slate-800 dark:bg-slate-900/50 lg:w-72 lg:border-l lg:border-t-0">
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">Ação administrativa</p>
                {form.bloqueada ? (
                  <>
                    <p className="mt-2 text-sm font-semibold text-red-700 dark:text-red-300">Instituição bloqueada</p>
                    <p className="mt-1 line-clamp-2 text-xs text-slate-600 dark:text-slate-400">{form.motivoBloqueio}</p>
                    <button type="button" onClick={handleDesbloquearInstituicao} disabled={desbloqueando} className="mt-3 text-sm font-semibold text-red-700 hover:underline disabled:opacity-50 dark:text-red-300">
                      {desbloqueando ? 'Desbloqueando...' : 'Desbloquear instituição'}
                    </button>
                  </>
                ) : (
                  <>
                    <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">Use somente quando houver pendência de prestação de contas.</p>
                    <button type="button" onClick={() => setShowBloqueioModal(true)} className="mt-3 text-sm font-semibold text-red-700 hover:underline dark:text-red-300">
                      Bloquear instituição
                    </button>
                  </>
                )}
              </aside>
            )}
          </div>
        </section>
      )}

      {/* Modal de reprovação */}
      {showReprovacaoModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-slate-950/40" onClick={() => setShowReprovacaoModal(false)} />
          <div className="relative z-10 w-full max-w-lg rounded-3xl bg-white p-6 shadow-xl">
            <h2 className="text-xl font-bold text-slate-900 mb-1">Reprovar instituição</h2>
            <p className="text-sm text-slate-500 mb-4">Informe o motivo da reprovação. O OPERADOR será notificado.</p>
            <textarea
              value={motivoReprovacao}
              onChange={(e) => setMotivoReprovacao(toUpperText(e.target.value))}
              rows={4}
              placeholder="Descreva o motivo da reprovação..."
              className="w-full resize-none rounded-2xl border border-slate-300 px-4 py-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-red-400"
            />
            <div className="mt-4 flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={() => { setShowReprovacaoModal(false); setMotivoReprovacao(''); }}
                className="flex-1 rounded-xl border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleConfirmarReprovacaoInstituicao}
                disabled={reprovandoInstituicao || !motivoReprovacao.trim()}
                className="flex-1 rounded-xl bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                {reprovandoInstituicao ? 'Reprovando...' : 'Confirmar reprovação'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de bloqueio */}
      {showBloqueioModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-slate-950/40" onClick={() => setShowBloqueioModal(false)} />
          <div className="relative z-10 w-full max-w-lg rounded-3xl bg-white p-6 shadow-xl dark:bg-slate-900">
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-1">Bloquear instituição</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
              Informe o motivo do bloqueio. A instituição verá este aviso na área de trabalho dela.
            </p>
            <textarea
              value={motivoBloqueio}
              onChange={(e) => setMotivoBloqueio(toUpperText(e.target.value))}
              rows={4}
              placeholder="Ex.: Prestação de contas pendente do plano de trabalho XX..."
              className="w-full resize-none rounded-2xl border border-slate-300 px-4 py-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-red-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
            />
            <div className="mt-4 flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={() => { setShowBloqueioModal(false); setMotivoBloqueio(''); }}
                className="flex-1 rounded-xl border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleConfirmarBloqueioInstituicao}
                disabled={bloqueando || !motivoBloqueio.trim()}
                className="flex-1 rounded-xl bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                {bloqueando ? 'Bloqueando...' : 'Confirmar bloqueio'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de inativação de vínculo (informar data de término do cargo) */}
      {showInativarVinculoModal && inativandoVinculo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-slate-950/40" onClick={() => setShowInativarVinculoModal(false)} />
          <div className="relative z-10 w-full max-w-lg rounded-3xl bg-white p-6 shadow-xl dark:bg-slate-900">
            <h2 className="mb-1 text-xl font-bold text-slate-900 dark:text-white">Informar término do cargo</h2>
            <p className="mb-4 text-sm text-slate-500 dark:text-slate-400">
              Encerra o vínculo de <strong>{inativandoVinculo.nomeCompleto}</strong> como{' '}
              <strong>{inativandoVinculo.cargo}</strong> nesta instituição. Vínculos da mesma pessoa em outras
              instituições não são afetados.
            </p>

            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200">Data de término *</label>
                <input
                  type="date"
                  value={inativarVinculoData.dataTermino}
                  onChange={(e) => setInativarVinculoData({ ...inativarVinculoData, dataTermino: e.target.value })}
                  className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/20 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200">Motivo *</label>
                <textarea
                  value={inativarVinculoData.motivo}
                  onChange={(e) => setInativarVinculoData({ ...inativarVinculoData, motivo: toUpperText(e.target.value) })}
                  rows={4}
                  placeholder="Descreva o motivo do encerramento (ex.: fim de mandato, renúncia, destituição)..."
                  className="w-full resize-none rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/20 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                />
              </div>
            </div>

            <div className="mt-5 flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={() => { setShowInativarVinculoModal(false); setInativandoVinculo(null); }}
                className={`${secondaryButtonClass} flex-1`}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleConfirmInativarVinculo}
                disabled={inativarVinculoLoading || !inativarVinculoData.dataTermino || !inativarVinculoData.motivo.trim()}
                className="inline-flex flex-1 items-center justify-center rounded-xl bg-amber-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {inativarVinculoLoading ? 'Encerrando...' : 'Confirmar término'}
              </button>
            </div>
          </div>
        </div>
      )}

      {editId && (cadastroObrigatorioFaltantes.length > 0 || dirigentesObrigatoriosFaltantes.length > 0 || dirigenteDocumentosPendentes.length > 0) && (
        <section className={`${panelClass} border-l-4 border-l-amber-400 px-4 py-3`} aria-label="Pendências para aprovação">
          <div className="flex flex-col gap-2 lg:flex-row lg:items-center">
            <div className="flex shrink-0 items-center gap-2">
              <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-amber-50 text-amber-600 dark:bg-amber-950/40 dark:text-amber-300">
                <ExclamationTriangleIcon className="h-4 w-4" aria-hidden="true" />
              </span>
              <div>
                <h2 className="text-sm font-semibold text-slate-950 dark:text-white">Pendências para aprovação</h2>
                <p className="text-[11px] text-slate-500 dark:text-slate-400">Regularize para avançar na tramitação.</p>
              </div>
            </div>

            <div className="flex flex-1 flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-600 dark:text-slate-300 lg:border-l lg:border-slate-200 lg:pl-4 dark:lg:border-slate-800">
              {cadastroObrigatorioFaltantes.length > 0 && (
                <span><strong className="font-semibold text-slate-800 dark:text-slate-100">Cadastro:</strong> {cadastroObrigatorioFaltantes.length} campo(s)</span>
              )}
              {dirigentesObrigatoriosFaltantes.length > 0 && (
                <span><strong className="font-semibold text-slate-800 dark:text-slate-100">Diretoria:</strong> {dirigentesObrigatoriosFaltantes.join(' e ')}</span>
              )}
              {dashboard.obrigatoriosFaltando > 0 && (
                <span><strong className="font-semibold text-slate-800 dark:text-slate-100">Documentos:</strong> {dashboard.obrigatoriosFaltando} obrigatório(s)</span>
              )}
              {totalPendenciasDirigentes > 0 && (
                <span><strong className="font-semibold text-slate-800 dark:text-slate-100">Dirigentes:</strong> {totalPendenciasDirigentes} documento(s)</span>
              )}
            </div>

            <span className="w-fit shrink-0 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700 dark:bg-amber-950/40 dark:text-amber-200">
              {totalPendenciasObrigatorias} pendência(s)
            </span>
          </div>

          {!loadingDocumentosDirigentes && dirigenteDocumentosPendentes.length > 0 && (
            <div className="mt-2 flex flex-wrap items-center gap-1.5 border-t border-slate-200 pt-2 text-[11px] dark:border-slate-800">
              <span className="mr-1 font-medium text-slate-500 dark:text-slate-400">Com pendências:</span>
              {dirigenteDocumentosPendentes.slice(0, 3).map((dirigente) => (
                <span key={`${dirigente.dirigenteId}-${dirigente.cargo}`} className="rounded-full bg-slate-100 px-2 py-1 text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                  {dirigente.nomeCompleto} · {dirigente.cargo}
                </span>
              ))}
              {dirigenteDocumentosPendentes.length > 3 && (
                <span className="text-slate-500 dark:text-slate-400">+{dirigenteDocumentosPendentes.length - 3}</span>
              )}
            </div>
          )}
        </section>
      )}

      {loadingExisting && (
        <div className="rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-700 shadow-sm dark:border-blue-500/40 dark:bg-blue-950/30 dark:text-blue-200">
          Carregando dados da instituição...
        </div>
      )}

      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 shadow-sm dark:border-red-500/40 dark:bg-red-950/30 dark:text-red-200">{error}</div>
      )}
      {success && (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 shadow-sm dark:border-emerald-500/40 dark:bg-emerald-950/30 dark:text-emerald-200">
          {success}
        </div>
      )}

      {/* Sistema de Abas */}
      <div className={`${panelClass} overflow-x-auto p-2`}>
          <nav aria-label="Seções da instituição" className="flex min-w-max gap-1.5">
            <button
              type="button"
              onClick={() => handleTabChange('instituicao')}
              className={getTabSeverityClasses(instituicaoTabSeverity, activeTab === 'instituicao')}
            >
              <span className="inline-flex items-center gap-2">
                <span className={`h-2 w-2 rounded-full ${getSeverityDotClass(instituicaoTabSeverity)}`}></span>
                Dados da Instituição
              </span>
            </button>
            <button
              type="button"
              onClick={() => handleTabChange('dirigentes')}
              className={getTabSeverityClasses(dirigentesTabSeverity, activeTab === 'dirigentes')}
            >
              <span className="inline-flex items-center gap-2">
                <span className={`h-2 w-2 rounded-full ${getSeverityDotClass(dirigentesTabSeverity)}`}></span>
                Diretoria ({dirigentes.length})
              </span>
            </button>
            <button
              type="button"
              onClick={() => handleTabChange('documentos')}
              className={getTabSeverityClasses(documentosTabSeverity, activeTab === 'documentos')}
            >
              <span className="inline-flex items-center gap-2">
                <span className={`h-2 w-2 rounded-full ${getSeverityDotClass(documentosTabSeverity)}`}></span>
                Documentos ({documentosNaoDeclaracoes.length}{documentosNaoDeclaracoes.filter(d => isDocumentoReprovado(d.statusDocumento)).length > 0 ? ` +${documentosNaoDeclaracoes.filter(d => isDocumentoReprovado(d.statusDocumento)).length} reprovados` : ""})
              </span>
            </button>
            <button
              type="button"
              onClick={() => handleTabChange("declaracoes")}
              className={`inline-flex rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                TIPOS_DECLARACAO.some(t => isDocumentoReprovado(documentosAtuaisPorTipo.get(t)?.statusDocumento))
                  ? activeTab === "declaracoes" ? "border-red-400 bg-red-50 text-red-900 dark:border-red-500/50 dark:bg-red-950/30 dark:text-red-200" : "border-red-300 bg-white text-red-700 hover:bg-red-50 dark:border-red-500/40 dark:bg-slate-950 dark:text-red-300 dark:hover:bg-red-950/20"
                  : TIPOS_DECLARACAO.some(t => !documentosAtuaisPorTipo.has(t))
                  ? activeTab === "declaracoes" ? "border-yellow-400 bg-yellow-50 text-yellow-900 dark:border-yellow-500/50 dark:bg-yellow-950/30 dark:text-yellow-200" : "border-yellow-300 bg-white text-yellow-700 hover:bg-yellow-50 dark:border-yellow-500/40 dark:bg-slate-950 dark:text-yellow-300 dark:hover:bg-yellow-950/20"
                  : activeTab === "declaracoes" ? `${isSaas ? "border-teal-400 bg-teal-50 text-teal-900 dark:border-teal-500/50 dark:bg-teal-950/30 dark:text-teal-200" : "border-blue-400 bg-blue-50 text-blue-900 dark:border-blue-500/50 dark:bg-blue-950/30 dark:text-blue-200"}` : "border-transparent text-slate-700 hover:border-slate-200 hover:bg-slate-50 dark:text-slate-300 dark:hover:border-slate-700 dark:hover:bg-slate-900"
              }`}
            >
              <span className="inline-flex items-center gap-2">
                <span className={`h-2 w-2 rounded-full ${
                  TIPOS_DECLARACAO.some(t => isDocumentoReprovado(documentosAtuaisPorTipo.get(t)?.statusDocumento))
                    ? "bg-red-500"
                    : TIPOS_DECLARACAO.some(t => !documentosAtuaisPorTipo.has(t))
                    ? "bg-yellow-500"
                    : "bg-emerald-500"
                }`}></span>
                Declarações ({documentosDeclaracoes.length}/{TIPOS_DECLARACAO.length})
              </span>
            </button>
          </nav>
      </div>

      {/* Aba 1: Dados da instituição */}
      {activeTab === 'instituicao' && (
      <form id="form-instituicao" onSubmit={handleSubmit} className="space-y-4">
        <fieldset disabled={!isEditMode} className="space-y-4">
        {isEditMode && editId && cadastroObrigatorioFaltantes.length > 0 && (
          <section className="rounded-2xl border border-orange-300 bg-orange-50 p-4 dark:border-orange-500/50 dark:bg-orange-950/20">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <h2 className="text-base font-semibold text-orange-900 dark:text-orange-100">Campos obrigatórios pendentes</h2>
                <p className="mt-1 text-sm text-orange-800 dark:text-orange-200">
                  Preencha os itens abaixo para liberar a aprovação do cadastro.
                </p>
              </div>
              <div className="inline-flex items-center rounded-full bg-white px-3 py-1 text-sm font-semibold text-orange-700 dark:bg-slate-950 dark:text-orange-200">
                {cadastroObrigatorioFaltantes.length} pendência(s)
              </div>
            </div>

            <div className="mt-3 flex flex-wrap gap-2">
              {cadastroObrigatorioFaltantes.map((field, index) => (
                <span
                  key={`${String(field.key)}-${field.label}-${index}`}
                  className="inline-flex items-center rounded-full border border-orange-200 bg-white px-3 py-1 text-sm font-medium text-orange-800 dark:border-orange-500/40 dark:bg-slate-950 dark:text-orange-200"
                >
                  {field.label}
                </span>
              ))}
            </div>
          </section>
        )}

        {/* CNPJ, Dados Básicos, Contato, Endereço e Informações Adicionais eram
            5 cards brancos separados empilhados (cada um com sua própria
            borda) — agora é um card só, dividido por linhas finas internas. */}
        <section className={`${panelClass} divide-y divide-slate-100 dark:divide-slate-800`}>
        <div className="p-4">
          <div className="mb-3 md:max-w-xs">
            <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">
              CNPJ *
              {!editId && (
                <button
                  type="button"
                  onClick={handleCnpjBlur}
                  disabled={loadingExisting || editId !== null || !(form.cnpj && onlyDigits(form.cnpj).length === 14 && isValidCnpj(onlyDigits(form.cnpj)))}
                  className={`${secondaryButtonClass} ml-3 px-3 py-1.5 text-xs`}
                >
                  Buscar
                </button>
              )}
            </label>
            <input
              type="text"
              inputMode="numeric"
              value={formatCnpj(form.cnpj ?? '')}
              onChange={(e) => {
                setField('cnpj', onlyDigits(e.target.value));
                // Reset status when user changes CNPJ
                if (cnpjSearchStatus !== 'initial' && cnpjSearchStatus !== 'searching') {
                  setCnpjSearchStatus('initial');
                  setFoundInstitution(null);
                }
              }}
              onBlur={handleCnpjBlur}
              disabled={loadingExisting || editId !== null}
              className={`${inputBaseClass} ${
                errors.cnpj
                  ? 'border-red-400 bg-red-50 dark:border-red-500/70 dark:bg-red-950/20'
                  : isEditMode && isRequiredInstitutionFieldMissing('cnpj')
                    ? 'border-orange-400 bg-orange-50 dark:border-orange-500/70 dark:bg-orange-950/20'
                    : isEditMode
                      ? 'border-orange-300 dark:border-orange-500/60'
                      : ''
              } ${loadingExisting ? 'bg-slate-50 dark:bg-slate-900' : ''}`}
              placeholder="00.000.000/0000-00"
              maxLength={18}
            />
            {loadingExisting && (
              <p className="mt-2 flex items-center text-sm text-blue-600 dark:text-blue-300">
                <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Buscando CNPJ...
              </p>
            )}
            {help('cnpj')}
          </div>

          {/* CNPJ ENCONTRADO: Mostrar card com botão Vincular */}
          {cnpjSearchStatus === 'found' && foundInstitution && (
            <div className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-5 dark:border-emerald-500/40 dark:bg-emerald-950/20">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                </div>
                <div className="ml-4 flex-1">
                  <h3 className="mb-2 text-lg font-semibold text-emerald-950 dark:text-emerald-100">
                    Instituição Encontrada!
                  </h3>
                  <div className="mb-4 rounded-2xl border border-emerald-200 bg-white p-4 dark:border-emerald-500/30 dark:bg-slate-950">
                    <p className="mb-2 text-sm text-slate-600 dark:text-slate-300">
                      <span className="font-semibold">Razão Social:</span> {foundInstitution.razaoSocial}
                    </p>
                    <p className="mb-2 text-sm text-slate-600 dark:text-slate-300">
                      <span className="font-semibold">CNPJ:</span> {formatCnpj(foundInstitution.cnpj)}
                    </p>
                    {foundInstitution.emailInstitucional && (
                      <p className="mb-2 text-sm text-slate-600 dark:text-slate-300">
                        <span className="font-semibold">Email:</span> {foundInstitution.emailInstitucional}
                      </p>
                    )}
                    {foundInstitution.telefone && (
                      <p className="text-sm text-slate-600 dark:text-slate-300">
                        <span className="font-semibold">Telefone:</span> {foundInstitution.telefone}
                      </p>
                    )}
                  </div>
                  <p className="mb-4 text-sm text-slate-700 dark:text-slate-200">
                    Esta instituição já está cadastrada no sistema. Se este login corresponde ao CNPJ dela, você pode vincular sua conta diretamente. Caso contrário, o acesso deve ser concedido por meio de uma procuração.
                  </p>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={handleVincular}
                      disabled={saving}
                      className={`${primaryButtonClass} px-6 py-3`}
                    >
                      {saving ? (
                        <>
                          <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Vinculando...
                        </>
                      ) : (
                        <>
                          Vincular à Minha Conta
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setCnpjSearchStatus('initial');
                        setFoundInstitution(null);
                        setField('cnpj', '');
                      }}
                      className={`${secondaryButtonClass} px-6 py-3`}
                    >
                      Buscar Outro CNPJ
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* CNPJ NÃO ENCONTRADO: Mostrar mensagem e continuar com formulário */}
          {cnpjSearchStatus === 'notFound' && (
            <div className="mt-4 rounded-2xl border border-blue-200 bg-blue-50 p-4 dark:border-blue-500/40 dark:bg-blue-950/30">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-blue-700 dark:text-blue-200">
                    <strong>CNPJ não encontrado no sistema.</strong> Preencha os dados abaixo para cadastrar esta instituição.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* MOSTRAR RESTO DO FORMULÁRIO apenas se CNPJ não foi encontrado OU está em modo de edição */}
        {(cnpjSearchStatus === 'notFound' || editId) && (
          <>
            {/* Dados Básicos */}
            <div className="p-4">
              <div className="mb-3 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                <h2 className={sectionTitleClass}>Dados Básicos</h2>
                {getMissingFieldsForSection('Dados Básicos').length > 0 && (
                  <span className="inline-flex items-center rounded-full bg-orange-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-orange-800 dark:bg-orange-950/40 dark:text-orange-200">
                    Pendências obrigatórias
                  </span>
                )}
              </div>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <label className={labelClass('razaoSocial')}>Razão Social *</label>
              <input
                type="text"
                maxLength={200}
                value={form.razaoSocial ?? ''}
                onChange={(e) => setField('razaoSocial', toUpperText(e.target.value))}
                className={fieldClass('razaoSocial')}
              />
              {help('razaoSocial')}
            </div>

            <div>
              <label className={labelClass('nomeFantasia')}>Nome Fantasia</label>
              <input
                type="text"
                maxLength={200}
                value={form.nomeFantasia ?? ''}
                onChange={(e) => setField('nomeFantasia', toUpperText(e.target.value))}
                className={fieldClass('nomeFantasia')}
              />
              {help('nomeFantasia')}
            </div>

            <div>
              <label className={labelClass('inscricaoEstadual')}>Inscrição Estadual</label>
              <input
                type="text"
                maxLength={20}
                value={form.inscricaoEstadual ?? ''}
                onChange={(e) => setField('inscricaoEstadual', toUpperText(e.target.value))}
                className={fieldClass('inscricaoEstadual')}
              />
              {help('inscricaoEstadual')}
            </div>

            <div>
              <label className={labelClass('inscricaoMunicipal')}>Inscrição Municipal *</label>
              <input
                type="text"
                maxLength={20}
                value={form.inscricaoMunicipal ?? ''}
                onChange={(e) => setField('inscricaoMunicipal', toUpperText(e.target.value))}
                className={fieldClass('inscricaoMunicipal')}
              />
              {help('inscricaoMunicipal')}
            </div>

            <div>
              <label className={labelClass('dataFundacao')}>Data de Fundação</label>
              <input
                type="date"
                value={form.dataFundacao ?? ''}
                onChange={(e) => setField('dataFundacao', e.target.value)}
                className={fieldClass('dataFundacao')}
              />
            </div>
          </div>

          <div className="mt-3">
            <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">Função / Área de Atuação</label>
            <select
              className={inputBaseClass}
              value=""
              onChange={(e) => {
                const code = e.target.value;
                if (code && !(form.areasAtuacao ?? []).includes(code)) {
                  handleToggleArea(code);
                }
              }}
            >
              <option value="">Selecione uma função / área de atuação...</option>
              {funcoesOrcamentarias
                .filter((f) => !(form.areasAtuacao ?? []).includes(f.codigo))
                .map((f) => (
                  <option key={f.codigo} value={f.codigo}>{f.descricao}</option>
                ))}
            </select>
            {(form.areasAtuacao ?? []).length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {(form.areasAtuacao ?? []).map((code) => {
                  const descricao = funcoesOrcamentarias.find((f) => f.codigo === code)?.descricao ?? code;
                  return (
                    <span
                      key={code}
                      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${isSaas ? 'bg-teal-100 text-teal-800 dark:bg-teal-950/40 dark:text-teal-200' : 'bg-blue-100 text-blue-800 dark:bg-blue-950/40 dark:text-blue-200'}`}
                    >
                      {descricao}
                      <button
                        type="button"
                        onClick={() => handleToggleArea(code)}
                        className={`ml-1 font-bold leading-none ${isSaas ? 'text-teal-700 hover:text-teal-950 dark:text-teal-200' : 'text-blue-700 hover:text-blue-950 dark:text-blue-200'}`}
                        title="Remover"
                      >
                        ×
                      </button>
                    </span>
                  );
                })}
              </div>
            )}
          </div>
            </div>

            {/* Dados de Contato */}
            <div className="p-4">
          <div className="mb-3 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <h2 className={sectionTitleClass}>Dados de Contato</h2>
            {getMissingFieldsForSection('Dados de Contato').length > 0 && (
              <span className="inline-flex items-center rounded-full bg-orange-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-orange-800 dark:bg-orange-950/40 dark:text-orange-200">
                Pendências obrigatórias
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <label className={labelClass('telefone')}>Telefone *</label>
              <input
                type="text"
                value={form.telefone ?? ''}
                onChange={(e) => setField('telefone', formatPhoneBR(e.target.value))}
                className={fieldClass('telefone')}
                placeholder="(00) 0000-0000"
              />
              {help('telefone')}
            </div>

            <div>
              <label className={labelClass('celular')}>Celular</label>
              <input
                type="text"
                value={form.celular ?? ''}
                onChange={(e) => setField('celular', formatPhoneBR(e.target.value))}
                className={fieldClass('celular')}
                placeholder="(00) 00000-0000"
              />
              {help('celular')}
            </div>

            <div>
              <label className={labelClass('emailInstitucional')}>E-mail Institucional *</label>
              <input
                type="email"
                value={form.emailInstitucional ?? ''}
                onChange={(e) => setField('emailInstitucional', e.target.value)}
                className={fieldClass('emailInstitucional')}
              />
              {help('emailInstitucional')}
            </div>

            <div>
              <label className={labelClass('emailSecundario')}>E-mail Secundário</label>
              <input
                type="email"
                value={form.emailSecundario ?? ''}
                onChange={(e) => setField('emailSecundario', e.target.value)}
                className={fieldClass('emailSecundario')}
              />
              {help('emailSecundario')}
            </div>

            <div className="md:col-span-2">
              <label className={labelClass('website')}>Website</label>
              <input
                type="url"
                value={form.website ?? ''}
                onChange={(e) => setField('website', e.target.value)}
                className={fieldClass('website')}
                placeholder="https://..."
              />
              {help('website')}
            </div>
          </div>
            </div>

            {/* Endereço */}
            <div className="p-4">
          <div className="mb-3 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <h2 className={sectionTitleClass}>Endereço</h2>
            {getMissingFieldsForSection('Endereço').length > 0 && (
              <span className="inline-flex items-center rounded-full bg-orange-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-orange-800 dark:bg-orange-950/40 dark:text-orange-200">
                Pendências obrigatórias
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
            <div className="md:col-span-2">
              <label className={labelClass('cep')}>CEP *</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={formatCep(form.cep ?? '')}
                  onChange={(e) => setField('cep', e.target.value)}
                  className={fieldClass('cep')}
                  placeholder="00000-000"
                />
                <button
                  type="button"
                  onClick={handleCepBlur}
                  disabled={loadingCep}
                  className={`${secondaryButtonClass} px-3`}
                >
                  {loadingCep ? 'Buscando...' : 'Buscar CEP'}
                </button>
              </div>
              {help('cep')}
            </div>

            <div className="md:col-span-2">
              <label className={labelClass('logradouro')}>Logradouro *</label>
              <input
                type="text"
                value={form.logradouro ?? ''}
                onChange={(e) => setField('logradouro', toUpperText(e.target.value))}
                className={fieldClass('logradouro')}
              />
              {help('logradouro')}
            </div>

            <div>
              <label className={labelClass('numero')}>Número *</label>
              <input
                type="text"
                value={form.numero ?? ''}
                onChange={(e) => setField('numero', toUpperText(e.target.value))}
                className={fieldClass('numero')}
              />
              {help('numero')}
            </div>

            <div>
              <label className={labelClass('complemento')}>Complemento</label>
              <input
                type="text"
                value={form.complemento ?? ''}
                onChange={(e) => setField('complemento', toUpperText(e.target.value))}
                className={fieldClass('complemento')}
              />
            </div>

            <div>
              <label className={labelClass('bairro')}>Bairro *</label>
              <input
                type="text"
                value={form.bairro ?? ''}
                onChange={(e) => setField('bairro', toUpperText(e.target.value))}
                className={fieldClass('bairro')}
              />
              {help('bairro')}
            </div>

            <div>
              <label className={labelClass('cidade')}>Cidade *</label>
              <input
                type="text"
                value={form.cidade ?? ''}
                onChange={(e) => setField('cidade', toUpperText(e.target.value))}
                className={fieldClass('cidade')}
              />
              {help('cidade')}
            </div>

            <div>
              <label className={labelClass('uf')}>UF *</label>
              <select
                value={form.uf ?? ''}
                onChange={(e) => setField('uf', e.target.value)}
                className={fieldClass('uf')}
              >
                <option value="">Selecione</option>
                {UFS.map((u) => (
                  <option key={u} value={u}>
                    {u}
                  </option>
                ))}
              </select>
              {help('uf')}
            </div>

            <div className="md:col-span-4">
              <label className={labelClass('pontoReferencia')}>Ponto de Referência</label>
              <input
                type="text"
                value={form.pontoReferencia ?? ''}
                onChange={(e) => setField('pontoReferencia', toUpperText(e.target.value))}
                className={fieldClass('pontoReferencia')}
              />
            </div>
          </div>
            </div>

            {/* Informações Adicionais */}
            <div className="p-4">
          <div className="mb-3 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <h2 className={sectionTitleClass}>Informações Adicionais</h2>
            {getMissingFieldsForSection('Informações Adicionais').length > 0 && (
              <span className="inline-flex items-center rounded-full bg-orange-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-orange-800 dark:bg-orange-950/40 dark:text-orange-200">
                Pendências obrigatórias
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <label className={labelClass('numeroRegistroConselhoMunicipal')}>
                Nº de Registro no Conselho Municipal *
              </label>
              <input
                type="text"
                value={form.numeroRegistroConselhoMunicipal ?? ''}
                onChange={(e) => setField('numeroRegistroConselhoMunicipal', toUpperText(e.target.value))}
                className={fieldClass('numeroRegistroConselhoMunicipal')}
              />
              {help('numeroRegistroConselhoMunicipal')}
            </div>

            <div>
              <label className={labelClass('dataRegistroConselho')}>Data de Registro no Conselho</label>
              <input
                type="date"
                value={form.dataRegistroConselho ?? ''}
                onChange={(e) => setField('dataRegistroConselho', e.target.value)}
                className={fieldClass('dataRegistroConselho')}
              />
            </div>

            <div className="md:col-span-2">
              <label className={labelClass('objetoSocial')}>Objeto Social *</label>
              <textarea
                value={form.objetoSocial ?? ''}
                onChange={(e) => setField('objetoSocial', toUpperText(e.target.value))}
                className={fieldClass('objetoSocial')}
                rows={3}
              />
              {help('objetoSocial')}
            </div>

            <div>
              <label className={labelClass('quantidadeBeneficiarios')}>
                Quantidade de Beneficiários Atendidos
              </label>
              <input
                type="number"
                min={0}
                value={form.quantidadeBeneficiarios ?? ''}
                onChange={(e) => setField('quantidadeBeneficiarios', e.target.value ? Number(e.target.value) : undefined)}
                className={fieldClass('quantidadeBeneficiarios')}
              />
              {help('quantidadeBeneficiarios')}
            </div>
          </div>
            </div>
          </>
        )}
        </section>

        {/* Save button only for NEW records â€” existing records use the tab bar buttons */}
        {!editId && cnpjSearchStatus === 'notFound' && (
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={saving}
              className={`${primaryButtonClass} px-5 py-2.5`}
            >
              {saving ? 'Salvando...' : 'Salvar Cadastro'}
            </button>
          </div>
        )}
        </fieldset>
      </form>
      )}

      {/* Aba 2: Diretoria */}
      {activeTab === 'dirigentes' && editId && (
        <div className="space-y-6">
          <section aria-label="Diretoria da instituição" className={`${panelClass} p-4 sm:p-5`}>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-950 dark:text-white">Gestão de Diretoria</h2>
                <p className={`mt-1 ${mutedTextClass}`}>Cadastre e acompanhe os dirigentes vinculados à instituição.</p>
              </div>
              <button
                onClick={() => navigate(`/dashboard/cadastro-dirigente?instituicaoId=${currentInstitutionId}`)}
                className={`${primaryButtonClass} shrink-0 self-start px-3 py-2`}
              >
                + Novo Dirigente
              </button>
            </div>
            <div className="mt-3 border-t border-slate-200 pt-3 dark:border-slate-800">
              <label className="inline-flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-200">
                <input
                  type="checkbox"
                  checked={!apenasAtivos}
                  onChange={(e) => setApenasAtivos(!e.target.checked)}
                  className="rounded border-slate-300 text-blue-700 focus:ring-blue-500"
                />
                <span>Mostrar inativos também</span>
              </label>
            </div>
          </section>

          {avisos.length > 0 && (
            <div className="rounded-2xl border border-orange-200 bg-orange-50 p-4 shadow-sm dark:border-orange-500/40 dark:bg-orange-950/20">
              <div className="flex gap-3">
                <div className="mt-0.5 flex-shrink-0">
                  <svg className="h-5 w-5 text-orange-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-orange-900 dark:text-orange-100">Cargos obrigatórios não preenchidos</h3>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {avisos.map((aviso, idx) => (
                      <span key={idx} className="rounded-full border border-orange-200 bg-white px-3 py-1 text-xs font-medium text-orange-800 dark:border-orange-500/40 dark:bg-slate-950 dark:text-orange-200">
                        {aviso}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {loadingDirigentes ? (
            <div className={`${panelClass} p-12 text-center`}>
              <p className="text-slate-500 dark:text-slate-400">Carregando dirigentes...</p>
            </div>
          ) : dirigentes.length === 0 ? (
            <div className={`${panelClass} p-12 text-center`}>
              <p className="text-lg font-semibold text-slate-950 dark:text-white">Nenhum dirigente cadastrado</p>
              <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">Adicione presidente, tesoureiro e demais membros da diretoria para continuar.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
              {dirigentes.map((dirigente, idx) => {
                const pendenciaDirigente = pendenciasDirigentePorPessoa.get(dirigente.pessoaId || dirigente.id || '');
                const possuiPendenciaDocumental = Boolean(
                  pendenciaDirigente
                  && (
                    pendenciaDirigente.faltantes.length > 0
                    || pendenciaDirigente.enviadosPendentes.length > 0
                    || pendenciaDirigente.reprovados.length > 0
                    || pendenciaDirigente.vencidos.length > 0
                  )
                );

                return (
                <div
                  key={`${dirigente.id || 'dir'}-${idx}`}
                  aria-label={`Dirigente ${dirigente.nomeCompleto}`}
                  className={`flex flex-col rounded-2xl border bg-white p-3 dark:bg-slate-950 ${
                    possuiPendenciaDocumental ? 'border-orange-200 dark:border-orange-500/40' : 'border-slate-200 dark:border-slate-800'
                  }`}
                >
                  <div className="mb-2 flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <h3 className="truncate text-sm font-semibold text-slate-950 dark:text-white">{dirigente.nomeCompleto}</h3>
                      <p className="text-xs text-slate-600 dark:text-slate-400">{dirigente.cargo}</p>
                      {possuiPendenciaDocumental && (
                        <span className={`mt-1.5 inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                          pendenciaDirigente?.vencidos.length || pendenciaDirigente?.reprovados.length
                            ? 'bg-red-100 text-red-700'
                            : pendenciaDirigente?.faltantes.length
                              ? 'bg-orange-100 text-orange-700'
                              : 'bg-blue-100 text-blue-700'
                        }`}>
                          {pendenciaDirigente?.vencidos.length
                            ? `${pendenciaDirigente.vencidos.length} documento${pendenciaDirigente.vencidos.length > 1 ? 's' : ''} vencido${pendenciaDirigente.vencidos.length > 1 ? 's' : ''}`
                            : pendenciaDirigente?.reprovados.length
                            ? 'Documentos com reprovação'
                            : pendenciaDirigente?.faltantes.length
                              ? 'Faltam documentos'
                              : 'Documentos em análise'}
                        </span>
                      )}
                    </div>
                    <span
                      className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
                        dirigente.statusCargo === 'Ativo'
                          ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200'
                          : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200'
                      }`}
                    >
                      {dirigente.statusCargo}
                    </span>
                  </div>

                  <div className="mb-2 flex-grow space-y-1.5 text-xs text-slate-600 dark:text-slate-300">
                    <div className="flex min-w-0 items-center">
                        <svg className="mr-1.5 h-3.5 w-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        <span className="truncate">{dirigente.email || 'E-mail não informado'}</span>
                    </div>
                    <div className="flex items-center">
                        <svg className="mr-1.5 h-3.5 w-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        <span>{dirigente.telefone || 'Telefone não informado'}</span>
                    </div>
                    <p className="border-t border-slate-100 pt-2 dark:border-slate-800">
                      <strong className="font-medium text-slate-800 dark:text-slate-200">Início:</strong> {new Date(dirigente.dataInicioCargo).toLocaleDateString('pt-BR')}
                    </p>
                  </div>

                  <div className="mt-auto space-y-1.5">
                    {Boolean(pendenciaDirigente?.vencidos.length) && (
                      <button
                        type="button"
                        onClick={() => handleOpenDocumentosPessoais(dirigente)}
                        className="flex w-full items-center justify-between rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-left text-xs font-semibold text-red-700 transition hover:bg-red-100 dark:border-red-500/40 dark:bg-red-950/20 dark:text-red-200 dark:hover:bg-red-950/40"
                        title={pendenciaDirigente?.vencidos.join(', ')}
                      >
                        <span>Documento do dirigente vencido</span>
                        <span>Renovar →</span>
                      </button>
                    )}
                    <div className="flex gap-1.5">
                      <button
                        onClick={() => navigate(`/dashboard/cadastro-dirigente?instituicaoId=${currentInstitutionId}&id=${dirigente.pessoaId || dirigente.id}`)}
                        className="flex-1 rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                      >
                        Editar
                      </button>
                      <button
                        onClick={() => handleOpenDocumentosPessoais(dirigente)}
                        className={`${primaryButtonClass} flex-1 rounded-lg px-2.5 py-1.5 text-xs`}
                        title="Gerenciar documentos pessoais"
                      >
                        Documentos
                      </button>
                    </div>
                    {dirigente.statusCargo === 'Ativo' && (
                      <button
                        onClick={() => handleOpenInativarVinculo(dirigente)}
                        className="w-full py-1 text-center text-xs font-medium text-amber-700 transition-colors hover:text-amber-900 hover:underline dark:text-amber-300 dark:hover:text-amber-200"
                        title="Informar data de término do cargo nesta instituição"
                      >
                        Informar término
                      </button>
                    )}
                  </div>
                </div>
              )})}
            </div>
          )}
        </div>
      )}

      {/* Aba 3: Documentos */}
      {activeTab === 'documentos' && editId && (
        <div className="space-y-6">
          {/* Banner de documentos a vencer */}
          {documentosAVencer.length > 0 && (
            <div className="rounded-2xl border border-orange-200 bg-orange-50 p-4">
              <div className="flex items-center gap-2 mb-2">
                <svg className="h-5 w-5 text-orange-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-sm font-bold text-orange-800">
                  {documentosAVencer.length} documento{documentosAVencer.length > 1 ? 's' : ''} a vencer nos próximos 30 dias
                </span>
              </div>
              <div className="divide-y divide-orange-100">
                {documentosAVencer.map(doc => {
                  const dias = calcularDiasParaVencimento(doc.dataValidade as string);
                  return (
                    <div key={doc.id} className="flex items-center justify-between py-1.5 text-xs">
                      <span className="text-slate-700">
                        {TIPOS_DOCUMENTO_LABELS[doc.tipoDocumento as keyof typeof TIPOS_DOCUMENTO_LABELS] || doc.tipoDocumento}
                      </span>
                      <span className="font-semibold text-orange-700">
                        {dias === 0 ? 'Vence hoje' : `Vence em ${dias} dia${dias !== 1 ? 's' : ''}`}
                        <span className="ml-1 font-normal text-slate-400">
                          ({new Date((doc.dataValidade as string) + 'T00:00:00').toLocaleDateString('pt-BR')})
                        </span>
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Banner de Diligências recebidas */}
          {diligencias.length > 0 && (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
              <div className="flex items-center gap-2 mb-3">
                <svg className="h-5 w-5 text-amber-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                </svg>
                <span className="text-sm font-bold text-amber-800">
                  Diligências da Secretaria de Convênios
                  {diligencias.filter(d => !d.lida && !diligenciasLidas.has(d.id)).length > 0 && (
                    <span className="ml-2 rounded-full bg-amber-600 px-2 py-0.5 text-[10px] font-bold text-white">
                      {diligencias.filter(d => !d.lida && !diligenciasLidas.has(d.id)).length} nova{diligencias.filter(d => !d.lida && !diligenciasLidas.has(d.id)).length > 1 ? 's' : ''}
                    </span>
                  )}
                </span>
              </div>
              <div className="space-y-2">
                {diligencias.map(d => {
                  const isLida = d.lida || diligenciasLidas.has(d.id);
                  return (
                    <div key={d.id} className={`rounded-xl border px-4 py-3 text-sm ${isLida ? 'border-amber-100 bg-white' : 'border-amber-300 bg-amber-100'}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <p className={`leading-relaxed ${isLida ? 'text-slate-700' : 'text-amber-900 font-medium'}`}>{d.mensagem}</p>
                          <p className="mt-1 text-[11px] text-amber-600">
                            Enviado por {d.enviadoPor} em {new Date(d.enviadoEm).toLocaleDateString('pt-BR')}
                          </p>
                        </div>
                        {!isLida && (
                          <button
                            type="button"
                            onClick={() => marcarDiligenciaLida(d.id)}
                            className="flex-shrink-0 rounded-lg border border-amber-300 bg-white px-2 py-1 text-[11px] font-medium text-amber-700 hover:bg-amber-50"
                          >
                            Marcar como lida
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <section
            aria-label="Resumo dos documentos institucionais"
            className={`${panelClass} flex flex-col gap-3 p-4 lg:flex-row lg:items-center lg:justify-between`}
          >
            <div className="shrink-0">
              <h2 className="text-lg font-semibold text-slate-950 dark:text-white">Documentos institucionais</h2>
              <p className={mutedTextClass}>Envie e acompanhe os documentos obrigatórios.</p>
            </div>
            <div className="flex max-w-full overflow-x-auto rounded-xl border border-slate-200 bg-slate-50/70 dark:border-slate-700 dark:bg-slate-900/70">
              <div className="min-w-[88px] px-3 py-2">
                <span className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Anexados</span>
                <strong className="mt-0.5 block text-base text-slate-950 dark:text-white">{documentosNaoDeclaracoes.length}</strong>
              </div>
              <div className="min-w-[88px] border-l border-slate-200 px-3 py-2 dark:border-slate-700">
                <span className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Aprovados</span>
                <strong className="mt-0.5 block text-base text-emerald-700 dark:text-emerald-300">{documentosNaoDeclaracoes.filter((d) => isDocumentoAprovado(d.statusDocumento)).length}</strong>
              </div>
              <div className="min-w-[88px] border-l border-slate-200 px-3 py-2 dark:border-slate-700">
                <span className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Pendentes</span>
                <strong className="mt-0.5 block text-base text-blue-700 dark:text-blue-300">{dashboardDocsTab.pendentes}</strong>
              </div>
              <div className="min-w-[104px] border-l border-slate-200 px-3 py-2 dark:border-slate-700">
                <span className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Não enviados</span>
                <strong className="mt-0.5 block text-base text-red-600 dark:text-red-300">{tiposObrigatoriosPendentesNoSelect.length}</strong>
              </div>
              {dashboardDocsTab.reprovados > 0 && (
                <div className="min-w-[92px] border-l border-slate-200 px-3 py-2 dark:border-slate-700">
                  <span className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Reprovados</span>
                  <strong className="mt-0.5 block text-base text-red-600 dark:text-red-300">{dashboardDocsTab.reprovados}</strong>
                </div>
              )}
              <div className="min-w-[104px] border-l border-slate-200 px-3 py-2 dark:border-slate-700">
                <span className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Obrigatórios</span>
                <strong className={`mt-0.5 block text-base ${accentTextClass}`}>{dashboardDocsTab.percentualCompleto}%</strong>
              </div>
            </div>
          </section>

          {/* Alertas */}
          {dashboard.vencidos > 0 && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-3">
              <p className="text-sm text-red-800 font-medium">
                ⚠️ {dashboard.vencidos} documento{dashboard.vencidos > 1 ? 's' : ''} vencido{dashboard.vencidos > 1 ? 's' : ''}
              </p>
            </div>
          )}
          {dashboard.proximosVencimento > 0 && (
            <div className="rounded-xl border border-orange-200 bg-orange-50 p-3">
              <p className="text-sm text-orange-800 font-medium">
                ⚠️ {dashboard.proximosVencimento} documento{dashboard.proximosVencimento > 1 ? 's próximos' : ' próximo'} ao vencimento (30 dias)
              </p>
            </div>
          )}

          {loadingDocumentosDirigentes && (
            <div className="mt-2 rounded-2xl border border-amber-200 bg-amber-50 p-3">
              <p className="text-sm text-amber-800 font-medium">
                Verificando documentos pendentes dos dirigentes...
              </p>
            </div>
          )}
          {!loadingDocumentosDirigentes && dirigenteDocumentosPendentes.length > 0 && (
            <div className="mt-2 rounded-2xl border border-amber-200 bg-amber-50 p-3">
              <p className="text-sm text-amber-800 font-medium">
                {dirigenteDocumentosPendentes.length} dirigente(s) com pendências documentais
              </p>
              <div className="mt-2 space-y-2 text-xs text-amber-900">
                {dirigenteDocumentosPendentes.map((dirigente) => (
                  <div key={`${dirigente.dirigenteId}-${dirigente.cargo}`} className="rounded border border-amber-200 bg-white/70 px-3 py-2">
                    <p className="font-semibold">
                      {dirigente.nomeCompleto} ({dirigente.cargo})
                    </p>
                    {dirigente.faltantes.length > 0 && <p>Faltando: {dirigente.faltantes.join(', ')}</p>}
                    {dirigente.enviadosPendentes.length > 0 && <p>Em análise: {dirigente.enviadosPendentes.join(', ')}</p>}
                    {dirigente.reprovados.length > 0 && <p>Reenviar: {dirigente.reprovados.join(', ')}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Formulário de Upload */}
          <div aria-label="Upload de documento" className={panelClass}>
            <h3 className="flex items-center gap-2 border-b border-slate-200 px-4 py-3 text-sm font-semibold text-slate-950 dark:border-slate-800 dark:text-white">
              <ArrowUpTrayIcon className="h-4 w-4 text-slate-500 dark:text-slate-400" aria-hidden="true" />
              Upload de novo documento
            </h3>
            <form onSubmit={handleUploadDocumento} className="p-4">
              <div className="grid grid-cols-1 gap-x-4 gap-y-3 md:grid-cols-2 xl:grid-cols-4">
                <div className="md:col-span-2 xl:col-span-4">
                  <div className="flex items-center justify-between gap-3 mb-1">
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
                      Tipo de Documento * <span className="text-xs text-slate-500 dark:text-slate-400">(Selecione o tipo correto)</span>
                    </label>
                    <div className="flex items-center gap-2">
                      {tiposObrigatoriosPendentesNoSelect.length > 0 && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full border border-yellow-300">
                          <ExclamationTriangleIcon className="h-3.5 w-3.5" aria-hidden="true" />
                          {tiposObrigatoriosPendentesNoSelect.length} faltando
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => setShowInfoModal(true)}
                        disabled={!selectedDocumentoInfo}
                        className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-full border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 disabled:bg-gray-100 disabled:text-gray-400 disabled:border-gray-200 disabled:cursor-not-allowed"
                        title={selectedDocumentoInfo ? 'Ver orientacoes deste documento' : 'Selecione um tipo de documento para abrir as orientacoes'}
                      >
                        <InformationCircleIcon className="h-4 w-4" aria-hidden="true" />
                        Info
                      </button>
                    </div>
                  </div>
                  <div className="relative">
                    <select
                      required
                      disabled={loadingConfigs}
                      value={tipoDocumentoSelecionado}
                      onChange={(e) => handleTipoDocumentoChange(e.target.value)}
                      className={`${inputBaseClass} ${
                        tiposObrigatoriosPendentesNoSelect.length > 0
                          ? 'border-yellow-400 ring-2 ring-yellow-200 focus:ring-yellow-300'
                          : ''
                      }`}
                    >
                      <option value="">{loadingConfigs ? 'Carregando...' : 'Selecione o tipo de documento'}</option>
                      {(() => {
                        const obrigSource = (obrigatoriosFromConfig && obrigatoriosFromConfig.length > 0)
                          ? obrigatoriosFromConfig
                          : DOCUMENTOS_OBRIGATORIOS;
                        return (
                          <optgroup label="Documentos Institucionais">
                            {DOCUMENTOS_INSTITUICAO.filter((t) => !TIPOS_DECLARACAO.includes(t as TipoDocumentoInstitucional) && !["CERTIDAO_TRIBUTOS_FEDERAIS","CERTIDAO_TRIBUTOS_ESTADUAIS_PGE","CERTIFICADO_FGTS","CERTIDAO_DEBITOS_TRABALHISTAS","CERTIDAO_DEBITOS_MUNICIPAIS","CERTIDAO_TCE_INSTITUICAO"].includes(t)).map((tipo) => {
                              const isObrigatorio = obrigSource.includes(tipo as any);
                              const jaEnviado = isDocumentoValidoParaObrigatorio(documentosAtuaisPorTipo.get(tipo)?.statusDocumento);
                              const precisaReenviar = isDocumentoReprovado(documentosAtuaisPorTipo.get(tipo)?.statusDocumento);
                              const faltaEnviar = tiposObrigatoriosPendentes.includes(tipo);

                              return (
                                <option
                                  key={tipo}
                                  value={tipo}
                                  style={faltaEnviar ? {
                                    backgroundColor: '#fef3c7',
                                    fontWeight: '600',
                                    color: '#92400e'
                                  } : {}}
                                >
                                  {faltaEnviar ? '⚠️ ' : jaEnviado ? '✓ ' : ''}{TIPOS_DOCUMENTO_LABELS[tipo]}
                                  {isObrigatorio ? ' (Obrigatório)' : ''}
                                  {precisaReenviar ? ' - REENVIAR' : faltaEnviar ? ' - PENDENTE' : ''}
                                </option>
                              );
                            })}
                          </optgroup>
                        );
                      })()}
                      <optgroup label="Certidões Negativas">
                        {DOCUMENTOS_INSTITUICAO.slice(14)
                          .filter((tipo) => ![
                            'DECLARACAO_CAPACIDADE_TECNICA',
                            'COMPROVANTE_EXPERIENCIA',
                            'DECLARACAO_COMPOSICAO_QUADRO_DIRIGENTES',
                            'RELACAO_NOMINAL_DIRIGENTES',
                            'DECLARACAO_CONDICOES_INSTALACAO',
                            'DECLARACAO_NAO_INCORRE_IMPEDIMENTOS',
                            'DECLARACAO_NAO_CONTRATACAO',
                          ].includes(tipo as TipoDocumentoInstitucional))
                          .map((tipo) => {
                          const obrigSource = (obrigatoriosFromConfig && obrigatoriosFromConfig.length > 0)
                            ? obrigatoriosFromConfig
                            : DOCUMENTOS_OBRIGATORIOS;
                          const isObrigatorio = obrigSource.includes(tipo as any);
                          const jaEnviado = isDocumentoValidoParaObrigatorio(documentosAtuaisPorTipo.get(tipo)?.statusDocumento);
                          const precisaReenviar = isDocumentoReprovado(documentosAtuaisPorTipo.get(tipo)?.statusDocumento);
                          const faltaEnviar = tiposObrigatoriosPendentes.includes(tipo);

                          return (
                            <option
                              key={tipo}
                              value={tipo}
                              style={faltaEnviar ? {
                                backgroundColor: '#fef3c7',
                                fontWeight: '600',
                                color: '#92400e'
                              } : {}}
                            >
                              {faltaEnviar ? '⚠️ ' : jaEnviado ? '✓ ' : ''}{TIPOS_DOCUMENTO_LABELS[tipo]}
                              {isObrigatorio ? ' (Obrigatório)' : ''}
                              {precisaReenviar ? ' - REENVIAR' : faltaEnviar ? ' - PENDENTE' : ''}
                            </option>
                          );
                        })}
                      </optgroup>
                    </select>
                    {tiposObrigatoriosPendentesNoSelect.length > 0 && (
                      <div className="absolute right-10 top-1/2 -translate-y-1/2 pointer-events-none">
                        <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" aria-hidden="true" />
                      </div>
                    )}
                  </div>
                  {selectedDocConfig && (
                    <div className="mt-2 text-xs bg-blue-50 border border-blue-200 rounded p-2">
                      <p className="font-medium text-blue-900">Campos obrigatórios para este documento:</p>
                      <ul className="mt-1 space-y-0.5 text-blue-700">
                        {selectedDocConfig.numeroDocumentoObrigatorio && <li>• Número do Documento</li>}
                        {selectedDocConfig.dataEmissaoObrigatoria && <li>• Data de Emissão</li>}
                        {selectedDocConfig.dataValidadeObrigatoria && <li>• Data de Validade</li>}
                        {!selectedDocConfig.numeroDocumentoObrigatorio &&
                         !selectedDocConfig.dataEmissaoObrigatoria &&
                         !selectedDocConfig.dataValidadeObrigatoria && (
                          <li className="text-green-700">✓ Apenas o arquivo é obrigatório</li>
                        )}
                      </ul>
                    </div>
                  )}
                  {selectedDocumentoInfo && (
                    <div className={`mt-3 border rounded-lg p-3 ${selectedDocumentoInfo.accentClassName}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold">{selectedDocumentoInfo.titulo}</p>
                          <p className="text-xs mt-1 opacity-90">{selectedDocumentoInfo.descricao}</p>
                          <p className="text-xs mt-2 opacity-90">{selectedDocumentoInfo.orientacao}</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setShowInfoModal(true)}
                          className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-current/20 bg-white/70 hover:bg-white text-xs font-medium"
                        >
                          <InformationCircleIcon className="h-4 w-4" aria-hidden="true" />
                          Detalhes
                        </button>
                      </div>
                      {selectedDocumentoInfo.url && (
                        <a
                          href={selectedDocumentoInfo.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-3 inline-flex items-center gap-2 text-xs font-medium underline"
                        >
                          {selectedDocumentoInfo.ctaLabel ?? 'Abrir link oficial'}
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.5 6H19m0 0v5.5M19 6l-7.5 7.5M7 8H5a2 2 0 00-2 2v9a2 2 0 002 2h9a2 2 0 002-2v-2" />
                          </svg>
                        </a>
                      )}
                    </div>
                  )}
                </div>

                <div className="xl:col-span-2">
                  <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200">
                    Arquivo * <span className="text-xs text-slate-500 dark:text-slate-400">(Máx: 20MB - PDF, JPG, PNG)</span>
                  </label>
                  <input
                    ref={uploadFileInputRef}
                    id="file-input-inst"
                    type="file"
                    required
                    onChange={handleFileSelect}
                    className={inputBaseClass}
                    accept={FORMATOS_ACEITOS.join(',')}
                  />
                  {selectedFile && (
                    <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                      ✓ {selectedFile.name} ({formatFileSize2(selectedFile.size)})
                    </p>
                  )}
                </div>

                <div className="xl:col-span-2">
                  <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200">
                    Número do Documento {selectedDocConfig?.numeroDocumentoObrigatorio ? (
                      <span className="text-red-600">*</span>
                    ) : (
                      <span className="text-xs text-slate-500 dark:text-slate-400">(opcional)</span>
                    )}
                  </label>
                  <input
                    type="text"
                    required={selectedDocConfig?.numeroDocumentoObrigatorio}
                    value={numeroDocumento}
                    onChange={(e) => setNumeroDocumento(toUpperText(e.target.value))}
                    className={`${inputBaseClass} ${selectedDocConfig?.numeroDocumentoObrigatorio ? 'border-blue-300 dark:border-blue-500/60' : ''}`}
                    placeholder="Ex: 123456789"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200">
                    Data de Emissão {selectedDocConfig?.dataEmissaoObrigatoria ? (
                      <span className="text-red-600">*</span>
                    ) : (
                      <span className="text-xs text-slate-500 dark:text-slate-400">(opcional)</span>
                    )}
                  </label>
                  <input
                    type="date"
                    required={selectedDocConfig?.dataEmissaoObrigatoria}
                    value={dataEmissao}
                    onChange={(e) => setDataEmissao(e.target.value)}
                    className={`${inputBaseClass} ${selectedDocConfig?.dataEmissaoObrigatoria ? 'border-blue-300 dark:border-blue-500/60' : ''}`}
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200">
                    Data de Validade {selectedDocConfig?.dataValidadeObrigatoria ? (
                      <span className="text-red-600">*</span>
                    ) : (
                      <span className="text-xs text-slate-500 dark:text-slate-400">(opcional)</span>
                    )}
                  </label>
                  <input
                    type="date"
                    required={selectedDocConfig?.dataValidadeObrigatoria}
                    value={dataValidade}
                    onChange={(e) => setDataValidade(e.target.value)}
                    className={`${inputBaseClass} ${selectedDocConfig?.dataValidadeObrigatoria ? 'border-blue-300 dark:border-blue-500/60' : ''}`}
                  />
                </div>

                <div className="md:col-span-2 xl:col-span-2">
                  <label className="mb-1 flex items-center justify-between text-sm font-medium text-slate-700 dark:text-slate-200">
                    <span>Observações <span className="text-xs font-normal text-slate-500 dark:text-slate-400">(opcional)</span></span>
                    <span className="text-xs font-normal text-slate-500 dark:text-slate-400">{observacoesDoc.length}/500</span>
                  </label>
                  <textarea
                    value={observacoesDoc}
                    onChange={(e) => setObservacoesDoc(toUpperText(e.target.value))}
                    className={inputBaseClass}
                    rows={1}
                    maxLength={500}
                    placeholder="Observações adicionais sobre o documento (opcional)"
                  />
                </div>
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  type="submit"
                  disabled={uploadingFile || !selectedFile || !tipoDocumentoSelecionado}
                  className={`${primaryButtonClass} gap-2`}
                >
                  {uploadingFile ? (
                    <>
                      <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Enviando...
                    </>
                  ) : (
                    <>
                      <ArrowUpTrayIcon className="h-4 w-4" aria-hidden="true" />
                      Enviar Documento
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>

          {/* Lista de Documentos */}
          <div aria-label="Documentos anexados" className={panelClass}>
            <div className="border-b border-slate-200 px-4 py-3 dark:border-slate-800">
              <h3 className="text-sm font-semibold text-slate-950 dark:text-white">Documentos anexados</h3>
              <p className={mutedTextClass}>
                {dashboard.total} documento{dashboard.total !== 1 ? 's' : ''} informado{dashboard.total !== 1 ? 's' : ''}{dashboard.reprovados > 0 ? ` • ${dashboard.reprovados} reprovado${dashboard.reprovados !== 1 ? 's' : ''}` : ''}
              </p>
            </div>

            <div className="p-4">
              {loadingDocumentos ? (
                 <div className="text-center py-12">
                   <svg className="animate-spin h-8 w-8 text-blue-600 mx-auto mb-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                     <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                     <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                   </svg>
                   <p className="text-gray-500">Carregando documentos...</p>
                 </div>
                ) : documentosNaoDeclaracoes.length === 0 ? (
                 <div className="flex items-center gap-3 rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-4 dark:border-slate-700 dark:bg-slate-900/70">
                   <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-950">
                     <DocumentIcon className="h-5 w-5 text-slate-400" aria-hidden="true" />
                   </div>
                   <div>
                     <p className="text-sm font-medium text-slate-700 dark:text-slate-200">Nenhum documento anexado</p>
                     <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">Envie o primeiro documento pelo formulário acima.</p>
                   </div>
                 </div>
               ) : (
                  <div className="space-y-3">
                    {/* Documentos visíveis */}
                    {documentosNaoDeclaracoes.length > 0 && (
                      <div>
                        <div className="space-y-2">
                          {documentosNaoDeclaracoes.map((doc) => (
                           <div
                             key={doc.id}
                             aria-label={`Documento ${TIPOS_DOCUMENTO_LABELS[doc.tipoDocumento]}`}
                             className={`overflow-hidden rounded-xl border transition-colors dark:bg-slate-950 ${isDocVencido(doc) ? 'border-red-300 bg-red-50/60 dark:border-red-500/50 dark:bg-red-950/20' : 'border-slate-200 bg-white dark:border-slate-800'}`}
                           >
                             <div className="flex items-start gap-2 p-2">
                               {/* Ícone */}
                               <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md bg-slate-100 text-sm dark:bg-slate-900">
                                 {getFileIcon2(doc.nomeArquivo)}
                               </div>

                               {/* Informações */}
                               <div className="flex-1 min-w-0">
                                 <div className="flex items-start justify-between gap-3">
                                   <div className="min-w-0 flex-1">
                                     <h5 className="text-sm font-semibold text-slate-950 dark:text-white">
                                       {TIPOS_DOCUMENTO_LABELS[doc.tipoDocumento]}
                                     </h5>
                                     <p className="truncate text-xs text-slate-500 dark:text-slate-400">{doc.nomeOriginal}</p>
                                   </div>
                                   {renderDocStatusBadge(doc.statusDocumento)}
                                 </div>

                                 <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-slate-600 dark:text-slate-300">
                                   <div className="whitespace-nowrap">
                                     <span className="text-slate-400 dark:text-slate-500">Tamanho:</span> {formatFileSize2(doc.tamanhoBytes)}
                                   </div>
                                   <div className="whitespace-nowrap">
                                     <span className="text-slate-400 dark:text-slate-500">Upload:</span> {new Date(doc.dataUpload).toLocaleDateString('pt-BR')}
                                   </div>
                                   {doc.dataEmissao && (
                                     <div className="whitespace-nowrap">
                                       <span className="text-slate-400 dark:text-slate-500">Emissão:</span> {new Date(doc.dataEmissao).toLocaleDateString('pt-BR')}
                                     </div>
                                   )}
                                   {doc.dataValidade && (
                                     <div className="whitespace-nowrap">
                                       <span className="text-slate-400 dark:text-slate-500">Validade:</span> {new Date(doc.dataValidade).toLocaleDateString('pt-BR')}
                                     </div>
                                   )}
                                   {doc.numeroDocumento && (
                                     <div className="whitespace-nowrap">
                                       <span className="text-slate-400 dark:text-slate-500">Nº:</span> {doc.numeroDocumento}
                                     </div>
                                   )}
                                 </div>

                                 {doc.dataValidade && renderAlertaVencimento(doc.dataValidade)}

                                 {doc.observacoes && (
                                   <p className="text-xs text-gray-600 mt-2 bg-gray-50 p-2 rounded">
                                     {doc.observacoes}
                                   </p>
                                 )}

                                {doc.motivoReprovacao && (
                                  <p className="text-xs text-red-600 mt-2 bg-red-50 p-2 rounded border-l-2 border-red-500">
                                     ❌ <strong>Motivo da Rejeição:</strong> {doc.motivoReprovacao}
                                  </p>
                                )}
                               </div>
                             </div>

                             {/* Ações */}
                             <div className="flex flex-col gap-1 border-t border-slate-100 bg-slate-50/70 px-2 py-1 dark:border-slate-800 dark:bg-slate-900/50 sm:flex-row sm:items-center sm:justify-between">
                               <div className="flex flex-wrap gap-1">
                                 <button
                                   onClick={() => handleViewDocumento(doc)}
                                   className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-1.5 py-0.5 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
                                 >
                                   <EyeIcon className="h-3 w-3" aria-hidden="true" />
                                   Visualizar
                                 </button>

                                 <button
                                   onClick={() => handleDownloadDocumento(doc.id!, doc.nomeOriginal)}
                                   className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-1.5 py-0.5 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
                                 >
                                   <ArrowDownTrayIcon className="h-3 w-3" aria-hidden="true" />
                                   Baixar
                                 </button>
                               </div>

                               <div className="flex flex-wrap gap-1 sm:justify-end">

                               {/* Aprovação documental é exclusiva de Convênios/Admin. */}
                               {canIniciarAnalise && !isDocumentoAprovado(doc.statusDocumento) && !isDocumentoReprovado(doc.statusDocumento) && (
                                 <button
                                   onClick={() => handleAprovarDocumento(doc.id!)}
                                   className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-1.5 py-0.5 text-xs font-semibold text-white transition-colors hover:bg-emerald-700"
                                 >
                                   <CheckIcon className="h-3 w-3" aria-hidden="true" />
                                   Aprovar
                                 </button>
                               )}

                               {/* Reprovação documental é exclusiva de Convênios/Admin. */}
                               {canIniciarAnalise && !isDocumentoReprovado(doc.statusDocumento) && !isDocumentoAprovado(doc.statusDocumento) && (
                                 <button
                                   onClick={() => handleReprovarDocumento(doc.id!)}
                                   className="inline-flex items-center gap-1 rounded-md border border-orange-300 bg-white px-1.5 py-0.5 text-xs font-medium text-orange-700 transition-colors hover:bg-orange-50 dark:border-orange-500/50 dark:bg-slate-950 dark:text-orange-300 dark:hover:bg-orange-950/30"
                                 >
                                   <XMarkIcon className="h-3 w-3" aria-hidden="true" />
                                   Reprovar
                                 </button>
                               )}

                              {/* Botão Renovar para todos os documentos não substituídos */}
                              {doc.statusDocumento !== 'SUBSTITUIDO' && (
                                <button
                                  onClick={() => handleAbrirRenovar(doc)}
                                  className={`inline-flex items-center gap-1 rounded-md border bg-white px-1.5 py-0.5 text-xs font-medium transition-colors dark:bg-slate-950 ${isDocVencido(doc) ? 'border-red-300 text-red-700 hover:bg-red-50 dark:border-red-500/50 dark:text-red-300 dark:hover:bg-red-950/30' : 'border-blue-300 text-blue-700 hover:bg-blue-50 dark:border-blue-500/50 dark:text-blue-300 dark:hover:bg-blue-950/30'}`}
                                >
                                  <ArrowPathIcon className="h-3 w-3" aria-hidden="true" />
                                  Renovar
                                </button>
                              )}

                              {/* Só permitir excluir se não estiver APROVADO */}
                              {!isDocumentoAprovado(doc.statusDocumento) && (
                                <button
                                  onClick={() => handleDeleteDocumento(doc.id!)}
                                  className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium text-red-600 transition-colors hover:bg-red-50 dark:text-red-300 dark:hover:bg-red-950/30"
                                >
                                  <TrashIcon className="h-3 w-3" aria-hidden="true" />
                                      Excluir
                                </button>
                              )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de Cadastro/Edição de Dirigente - REMOVIDO - Agora usa página separada */}

      {/* Drawer de Renovação de Documento Vencido */}
      {renovarDoc && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="fixed inset-0 bg-black/40" onClick={handleFecharRenovar} />
          <div className="relative z-10 flex h-full w-full max-w-md flex-col bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-5 py-4">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Renovar Documento</h3>
                <p className="mt-0.5 text-xs text-gray-500">{TIPOS_DOCUMENTO_LABELS[renovarDoc.tipoDocumento as TipoDocumentoInstitucional] || renovarDoc.tipoDocumento}</p>
              </div>
              <button type="button" onClick={handleFecharRenovar} className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmitRenovar} className="flex flex-1 flex-col overflow-y-auto p-5 gap-4">
              <div className={`rounded-lg border px-4 py-3 text-xs ${isDocVencido(renovarDoc) ? 'border-red-200 bg-red-50 text-red-700' : 'border-blue-200 bg-blue-50 text-blue-700'}`}>
                Faça o upload da nova versão de <strong>{renovarDoc.nomeOriginal}</strong>. O documento anterior será marcado como substituído.
              </div>

              {/* Arquivo */}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Novo arquivo *</label>
                <input
                  ref={renovarFileInputRef}
                  type="file"
                  accept={FORMATOS_ACEITOS}
                  className="hidden"
                  onChange={(e) => setRenovarFile(e.target.files?.[0] || null)}
                />
                <button
                  type="button"
                  onClick={() => renovarFileInputRef.current?.click()}
                  className="flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed border-gray-300 px-4 py-6 text-sm text-gray-500 hover:border-blue-400 hover:text-blue-600"
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                  {renovarFile ? renovarFile.name : 'Selecionar arquivo'}
                </button>
                {renovarFile && (
                  <p className="mt-1 text-xs text-gray-500">{formatFileSize2(renovarFile.size)}</p>
                )}
              </div>

              {/* Número do documento */}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Número do documento</label>
                <input
                  type="text"
                  value={renovarNumeroDocumento}
                  onChange={(e) => setRenovarNumeroDocumento(toUpperText(e.target.value))}
                  className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ex: 2025/001234"
                />
              </div>

              {/* Datas */}
              {(() => {
                const tipoConfig = documentConfigs.find(c => c.codigo === renovarDoc.tipoDocumento);
                return (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        Data de emissão {tipoConfig?.dataEmissaoObrigatoria && <span className="text-red-500">*</span>}
                      </label>
                      <input
                        type="date"
                        value={renovarDataEmissao}
                        onChange={(e) => setRenovarDataEmissao(e.target.value)}
                        required={tipoConfig?.dataEmissaoObrigatoria}
                        className={`w-full rounded border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${tipoConfig?.dataEmissaoObrigatoria ? 'border-blue-300' : 'border-gray-300'}`}
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        Nova validade {tipoConfig?.dataValidadeObrigatoria && <span className="text-red-500">*</span>}
                      </label>
                      <input
                        type="date"
                        value={renovarDataValidade}
                        onChange={(e) => setRenovarDataValidade(e.target.value)}
                        required={tipoConfig?.dataValidadeObrigatoria}
                        className={`w-full rounded border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${tipoConfig?.dataValidadeObrigatoria ? 'border-blue-300' : 'border-gray-300'}`}
                      />
                    </div>
                  </div>
                );
              })()}

              <div className="mt-auto flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={handleFecharRenovar}
                  className="flex-1 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={!renovarFile || renovarLoading}
                  className={`flex-1 rounded-lg px-4 py-2 text-sm font-medium text-white disabled:opacity-50 ${isDocVencido(renovarDoc) ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}`}
                >
                  {renovarLoading ? 'Enviando...' : 'Renovar documento'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Aba: Declarações */}
      {activeTab === "declaracoes" && editId && (
        <div className="space-y-4">
          <section aria-label="Declarações obrigatórias" className={`${panelClass} p-4 sm:p-5`}>
            <h2 className="text-lg font-semibold text-slate-950 dark:text-white">Declarações Obrigatórias</h2>
            <p className={`mt-1 ${mutedTextClass}`}>
              Envie as {TIPOS_DECLARACAO.length} declarações exigidas pela Lei Federal nº 13.019/2014, assinadas pelo representante legal.
            </p>
          </section>

          {/* Lista de declarações */}
          <div className="space-y-2">
            {TIPOS_DECLARACAO.map((tipo) => {
              const doc = documentosAtuaisPorTipo.get(tipo);
              const template = DECLARACAO_TEMPLATES[tipo];
              const isActive = activeDeclaracaoUpload === tipo;

              return (
                <div key={tipo} className="rounded-lg border border-gray-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-950">
                  {/* Cabeçalho do card */}
                  <div className="p-2 flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-slate-100">
                          {TIPOS_DOCUMENTO_LABELS[tipo]}
                        </h4>
                        {doc ? renderDocStatusBadge(doc.statusDocumento) : (
                          <span className="inline-flex items-center rounded-full border border-gray-200 bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-800 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200">
                            Pendente
                          </span>
                        )}
                      </div>
                      {doc && (
                        <p className="mt-0.5 truncate text-xs text-gray-500 dark:text-slate-400">
                          {doc.nomeOriginal} • Enviado em {new Date(doc.dataUpload).toLocaleDateString("pt-BR")}
                        </p>
                      )}
                      {doc?.motivoReprovacao && (
                        <p className="mt-1 rounded border-l-2 border-red-500 bg-red-50 p-2 text-xs text-red-600 dark:bg-red-950/30 dark:text-red-300">
                          ❌ <strong>Motivo da rejeição:</strong> {doc.motivoReprovacao}
                        </p>
                      )}
                      {doc && !doc.motivoReprovacao && doc.dataValidade && renderAlertaVencimento(doc.dataValidade)}
                    </div>

                    {/* Botões de template e upload */}
                    <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
                      {template && (
                        <a
                          href={template.url}
                          download
                          className="flex items-center gap-1 rounded border border-gray-300 bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700 hover:bg-gray-200 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          {template.label}
                        </a>
                      )}
                      {!doc && !isActive && (
                        <button
                          onClick={() => { setActiveDeclaracaoUpload(tipo); setSelectedFileDeclaracao(null); }}
                          className="px-2 py-0.5 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 flex items-center gap-1 font-medium"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                          </svg>
                          Enviar assinado
                        </button>
                      )}
                      {isActive && (
                        <button
                          onClick={() => { setActiveDeclaracaoUpload(null); setSelectedFileDeclaracao(null); }}
                          className="rounded bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-700 hover:bg-gray-300 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                        >
                          Cancelar
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Área de upload inline */}
                  {isActive && (
                    <div className="border-t border-gray-100 bg-gray-50 p-4 dark:border-slate-800 dark:bg-slate-900">
                      <div className="flex items-center gap-3">
                        <input
                          type="file"
                          accept={FORMATOS_ACEITOS.join(",")}
                          onChange={(e) => {
                            const file = e.target.files?.[0] ?? null;
                            if (file && typeof TAMANHO_MAXIMO_BYTES === "number" && file.size > Number(TAMANHO_MAXIMO_BYTES)) {
                              showToast(`Arquivo muito grande. Max: ${TAMANHO_MAXIMO_MB}MB`, "error");
                              e.target.value = "";
                              return;
                            }
                            setSelectedFileDeclaracao(file);
                          }}
                          className="flex-1 rounded border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                          disabled={uploadingDeclaracao}
                        />
                        <button
                          onClick={() => { if (selectedFileDeclaracao) handleUploadDeclaracao(tipo, selectedFileDeclaracao); }}
                          disabled={!selectedFileDeclaracao || uploadingDeclaracao}
                          className="px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-medium"
                        >
                          {uploadingDeclaracao ? (
                            <>
                              <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Enviando...
                            </>
                          ) : "Enviar"}
                        </button>
                      </div>
                      <p className="mt-1 text-xs text-gray-500 dark:text-slate-400">
                        Formatos aceitos: {FORMATOS_ACEITOS.join(", ")} • Max: {TAMANHO_MAXIMO_MB}MB
                      </p>
                    </div>
                  )}

                  {/* Ações para documento enviado */}
                  {doc && (
                    <div className="flex flex-col gap-1 border-t border-slate-100 bg-slate-50/70 px-2 py-1 dark:border-slate-800 dark:bg-slate-900/50 sm:flex-row sm:items-center sm:justify-between">
                      <div className="flex flex-wrap gap-1">
                        <button
                          onClick={() => handleViewDocumento(doc)}
                          className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-1.5 py-0.5 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
                        >
                          <EyeIcon className="h-3 w-3" aria-hidden="true" />
                          Visualizar
                        </button>
                        <button
                          onClick={() => handleDownloadDocumento(doc.id!, doc.nomeOriginal)}
                          className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-1.5 py-0.5 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
                        >
                          <ArrowDownTrayIcon className="h-3 w-3" aria-hidden="true" />
                          Baixar
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-1 sm:justify-end">
                        {canIniciarAnalise && !isDocumentoAprovado(doc.statusDocumento) && !isDocumentoReprovado(doc.statusDocumento) && (
                          <button
                            onClick={() => handleAprovarDocumento(doc.id!)}
                            className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-1.5 py-0.5 text-xs font-semibold text-white transition-colors hover:bg-emerald-700"
                          >
                            <CheckIcon className="h-3 w-3" aria-hidden="true" />
                            Aprovar
                          </button>
                        )}
                        {canIniciarAnalise && !isDocumentoReprovado(doc.statusDocumento) && !isDocumentoAprovado(doc.statusDocumento) && (
                          <button
                            onClick={() => handleReprovarDocumento(doc.id!)}
                            className="inline-flex items-center gap-1 rounded-md border border-orange-300 bg-white px-1.5 py-0.5 text-xs font-medium text-orange-700 transition-colors hover:bg-orange-50 dark:border-orange-500/50 dark:bg-slate-950 dark:text-orange-300 dark:hover:bg-orange-950/30"
                          >
                            <XMarkIcon className="h-3 w-3" aria-hidden="true" />
                            Reprovar
                          </button>
                        )}
                        {!isActive && (
                          <button
                            onClick={() => { setActiveDeclaracaoUpload(tipo); setSelectedFileDeclaracao(null); }}
                            className={`inline-flex items-center gap-1 rounded-md border bg-white px-1.5 py-0.5 text-xs font-medium transition-colors dark:bg-slate-950 ${isDocVencido(doc) ? 'border-red-300 text-red-700 hover:bg-red-50 dark:border-red-500/50 dark:text-red-300 dark:hover:bg-red-950/30' : 'border-blue-300 text-blue-700 hover:bg-blue-50 dark:border-blue-500/50 dark:text-blue-300 dark:hover:bg-blue-950/30'}`}
                          >
                            <ArrowPathIcon className="h-3 w-3" aria-hidden="true" />
                            Renovar
                          </button>
                        )}
                        {!isDocumentoAprovado(doc.statusDocumento) && (
                          <button
                            onClick={() => handleDeleteDocumento(doc.id!)}
                            className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium text-red-600 transition-colors hover:bg-red-50 dark:text-red-300 dark:hover:bg-red-950/30"
                          >
                            <TrashIcon className="h-3 w-3" aria-hidden="true" />
                            Excluir
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {showFileViewer && currentViewingDoc?.id && (
        <FileViewerModal
          url={documentoInstitucionalService.viewUrl(currentViewingDoc.id)}
          nome={currentViewingDoc.nomeOriginal}
          mimeType={currentViewingDoc.tipoMime}
          subtitle={`${TIPOS_DOCUMENTO_LABELS[currentViewingDoc.tipoDocumento]} · ${currentViewingIndex + 1} / ${documentosVisiveis.length}`}
          onClose={handleCloseFileViewer}
          onPrev={handlePreviousDocument}
          onNext={handleNextDocument}
          canPrev={currentViewingIndex > 0}
          canNext={currentViewingIndex < documentosVisiveis.length - 1}
        />
      )}

      {/* Modal de Documentos Pessoais do Dirigente */}
      {showDocumentosPessoaisModal && dirigenteDocumentosPessoais && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="my-8 w-full max-w-5xl rounded-3xl border border-slate-200 bg-white shadow-xl">
            <div className="flex justify-between items-center border-b border-slate-200 p-6">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700">Documentos</p>
                <h2 className="mt-2 text-xl font-bold text-slate-900">Documentos Pessoais</h2>
                <p className="mt-1 text-sm text-slate-600">
                  {dirigenteDocumentosPessoais.nomeCompleto} - {dirigenteDocumentosPessoais.cargo}
                </p>
              </div>
              <button
                type="button"
                onClick={handleCloseDocumentosPessoais}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ✕
              </button>
            </div>

            <div className="max-h-[70vh] overflow-y-auto p-6">
              <DocumentosPessoaisUpload
                dirigenteId={dirigenteDocumentosPessoais.pessoaId || dirigenteDocumentosPessoais.id!}
                cargo={dirigenteDocumentosPessoais.cargo}
                embedded
                onDocumentosChange={() => {
                  refreshInstitutionOverview();
                }}
              />
            </div>

            <div className="flex justify-end gap-3 border-t border-slate-200 bg-slate-50 p-6">
              <button
                type="button"
                onClick={handleCloseDocumentosPessoais}
                className="rounded-xl bg-emerald-600 px-6 py-2 text-sm font-medium text-white hover:bg-emerald-700"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de informações sobre links de renovação de documentos */}
      {showInfoModal && selectedDocumentoInfo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-xl w-full max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b">
              <div>
                <h2 className="text-xl font-bold">{selectedDocumentoInfo.titulo}</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Orientacoes para localizar o documento durante o preenchimento.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowInfoModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className={`border rounded-lg p-4 ${selectedDocumentoInfo.accentClassName}`}>
                <p className="text-sm font-semibold">{selectedDocumentoInfo.descricao}</p>
                <p className="text-sm mt-2 opacity-90">{selectedDocumentoInfo.orientacao}</p>
              </div>

              {selectedDocumentoInfo.url ? (
                <div className="border rounded-lg p-4 bg-white border-gray-200">
                  <p className="text-sm font-medium text-gray-900">Link oficial</p>
                  <a
                    href={selectedDocumentoInfo.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 inline-flex items-center gap-2 text-sm text-blue-700 hover:text-blue-900 underline break-all"
                  >
                    {selectedDocumentoInfo.url}
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.5 6H19m0 0v5.5M19 6l-7.5 7.5M7 8H5a2 2 0 00-2 2v9a2 2 0 002 2h9a2 2 0 002-2v-2" />
                    </svg>
                  </a>
                </div>
              ) : (
                <div className="border rounded-lg p-4 bg-gray-50 border-gray-200">
                  <p className="text-sm font-medium text-gray-900">Localizacao</p>
                  <p className="text-sm text-gray-700 mt-2">
                    Este documento nao possui portal externo configurado. Use a orientacao acima para localizar o arquivo correto.
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
              {selectedDocumentoInfo.url && (
                <a
                  href={selectedDocumentoInfo.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-6 py-2 border border-blue-200 text-blue-700 rounded-lg hover:bg-blue-50"
                >
                  {selectedDocumentoInfo.ctaLabel ?? 'Abrir link oficial'}
                </a>
              )}
              <button
                type="button"
                onClick={() => setShowInfoModal(false)}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {showInfoModal && !selectedDocumentoInfo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b">
              <div>
                <h2 className="text-xl font-bold">Links para Renovação de Documentos</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Links oficiais para emissão e renovação de documentos necessários
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowInfoModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ✕
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid gap-4">
                <div className="border rounded-lg p-4 bg-blue-50 border-blue-200">
                  <h3 className="font-semibold text-blue-900 mb-2">Cartão CNPJ</h3>
                  <p className="text-sm text-blue-700 mb-2">
                    Emissão e renovação do Cartão CNPJ
                  </p>
                  <a
                    href="https://solucoes.receita.fazenda.gov.br/servicos/cnpjreva/cnpjreva_solicitacao.asp"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 underline text-sm"
                  >
                    https://solucoes.receita.fazenda.gov.br/servicos/cnpjreva/cnpjreva_solicitacao.asp
                  </a>
                </div>

                <div className="border rounded-lg p-4 bg-green-50 border-green-200">
                  <h3 className="font-semibold text-green-900 mb-2">Estatuto Social</h3>
                  <p className="text-sm text-green-700 mb-2">
                    Renovação do Estatuto Social (Expediente e Atos)
                  </p>
                  <p className="text-green-600 text-sm">Renova no expediente e atos</p>
                </div>

                <div className="border rounded-lg p-4 bg-purple-50 border-purple-200">
                  <h3 className="font-semibold text-purple-900 mb-2">Alvará de Funcionamento</h3>
                  <p className="text-sm text-purple-700 mb-2">
                    Emissão e renovação do Alvará de Funcionamento
                  </p>
                  <a
                    href="https://www.gp.srv.br/tributario/sinop/portal_serv_servico?10,31"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-purple-600 hover:text-purple-800 underline text-sm"
                  >
                    https://www.gp.srv.br/tributario/sinop/portal_serv_servico?10,31
                  </a>
                </div>

                <div className="border rounded-lg p-4 bg-yellow-50 border-yellow-200">
                  <h3 className="font-semibold text-yellow-900 mb-2">Declaração de Utilidade Pública</h3>
                  <p className="text-sm text-yellow-700 mb-2">
                    Renovação da Declaração de Utilidade Pública
                  </p>
                  <p className="text-yellow-600 text-sm">Renova no expediente e atos</p>
                </div>

                <div className="border rounded-lg p-4 bg-indigo-50 border-indigo-200">
                  <h3 className="font-semibold text-indigo-900 mb-2">Ata da Última Eleição de Diretoria</h3>
                  <p className="text-sm text-indigo-700 mb-2">
                    Documento interno da instituição
                  </p>
                  <p className="text-indigo-600 text-sm">Documento interno da instituição</p>
                </div>

                <div className="border rounded-lg p-4 bg-red-50 border-red-200">
                  <h3 className="font-semibold text-red-900 mb-2">Comprovante de Inscrição no Conselho Estadual</h3>
                  <p className="text-sm text-red-700 mb-2">
                    Renovação do Comprovante de Inscrição
                  </p>
                  <p className="text-red-600 text-sm">Renova no expediente e atos</p>
                </div>

                <div className="border rounded-lg p-4 bg-gray-50 border-gray-200">
                  <h3 className="font-semibold text-gray-900 mb-2">Comprovante de Endereço</h3>
                  <p className="text-sm text-gray-700 mb-2">
                    Documento comprobatório de endereço
                  </p>
                  <p className="text-gray-600 text-sm">Conta de luz, água, telefone, etc.</p>
                </div>

                <div className="border rounded-lg p-4 bg-orange-50 border-orange-200">
                  <h3 className="font-semibold text-orange-900 mb-2">Certidão Negativa de Tributos Federais</h3>
                  <p className="text-sm text-orange-700 mb-2">
                    Emissão de Certidão Negativa de Débitos Federais
                  </p>
                  <a
                    href="https://servicos.receitafederal.gov.br/servico/certidoes/#/home/cnpj"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-orange-600 hover:text-orange-800 underline text-sm"
                  >
                    https://servicos.receitafederal.gov.br/servico/certidoes/#/home/cnpj
                  </a>
                </div>

                <div className="border rounded-lg p-4 bg-teal-50 border-teal-200">
                  <h3 className="font-semibold text-teal-900 mb-2">Certidão Negativa de Tributos Estaduais</h3>
                  <p className="text-sm text-teal-700 mb-2">
                    Emissão de Certidão Negativa de Débitos Estaduais (MT)
                  </p>
                  <a
                    href="https://www.sefaz.mt.gov.br/cnd/certidao/servlet/ServletRotd?origem=60"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-teal-600 hover:text-teal-800 underline text-sm"
                  >
                    https://www.sefaz.mt.gov.br/cnd/certidao/servlet/ServletRotd?origem=60
                  </a>
                </div>

                <div className="border rounded-lg p-4 bg-pink-50 border-pink-200">
                  <h3 className="font-semibold text-pink-900 mb-2">Certidão de Regularidade do FGTS</h3>
                  <p className="text-sm text-pink-700 mb-2">
                    Emissão de Certidão de Regularidade do FGTS
                  </p>
                  <a
                    href="https://consulta-crf.caixa.gov.br/consultacrf/pages/consultaEmpregador.jsf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-pink-600 hover:text-pink-800 underline text-sm"
                  >
                    https://consulta-crf.caixa.gov.br/consultacrf/pages/consultaEmpregador.jsf
                  </a>
                </div>

                <div className="border rounded-lg p-4 bg-cyan-50 border-cyan-200">
                  <h3 className="font-semibold text-cyan-900 mb-2">Certidão Negativa de Débitos Trabalhistas</h3>
                  <p className="text-sm text-cyan-700 mb-2">
                    Emissão de Certidão Negativa de Débitos Trabalhistas
                  </p>
                  <a
                    href="https://cndt-certidao.tst.jus.br/inicio.faces"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-cyan-600 hover:text-cyan-800 underline text-sm"
                  >
                    https://cndt-certidao.tst.jus.br/inicio.faces
                  </a>
                </div>

                <div className="border rounded-lg p-4 bg-lime-50 border-lime-200">
                  <h3 className="font-semibold text-lime-900 mb-2">Certidão Negativa de Débitos Municipais</h3>
                  <p className="text-sm text-lime-700 mb-2">
                    Emissão de Certidão Negativa de Débitos Municipais (Sinop)
                  </p>
                  <a
                    href="https://www.gp.srv.br/tributario/sinop/portal_serv_servico?12,53"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-lime-600 hover:text-lime-800 underline text-sm"
                  >
                    https://www.gp.srv.br/tributario/sinop/portal_serv_servico?12,53
                  </a>
                </div>

                <div className="border rounded-lg p-4 bg-violet-50 border-violet-200">
                  <h3 className="font-semibold text-violet-900 mb-2">Certidão de Regularidade do TCE/MT</h3>
                  <p className="text-sm text-violet-700 mb-2">
                    Emissão de Certidão de Regularidade do Tribunal de Contas (MT)
                  </p>
                  <a
                    href="https://servicos.tce.mt.gov.br/certidao/emissao"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-violet-600 hover:text-violet-800 underline text-sm"
                  >
                    https://servicos.tce.mt.gov.br/certidao/emissao
                  </a>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
              <button
                type="button"
                onClick={() => setShowInfoModal(false)}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};

export default CadastroDadosInstitucionaisPage;



